import { useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { useLocationScope } from "@/hooks/useLocationScope";
import { claveAlcance } from "@/lib/alcance";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateScoutingData } from "@/lib/scoutingDataInvalidation";
import { useQuery } from "@tanstack/react-query";
import {
  Camera,
  Images,
  Mic,
  Square,
  Trash2,
  X,
  Plus,
  Loader2,
  Check,
  MapPin,
  ChevronRight,
  Warehouse,
  Grid3x3,
  Rows3,
  Bug,
  Gauge,
  Pencil,
  CircleHelp,
  AlertTriangle,
  ClipboardList,
  type LucideIcon,
} from "lucide-react";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useGpsPlacement, type GpsPlacement } from "@/hooks/useGpsPlacement";
import { gpsMismatchOf, matchSpokenBlock, mergeVoiceAndGps, voiceGuessOf, VOICE_PLACE_UNRELIABLE_LANGS, type GpsMismatch, type MergedPlace, type PlacedBy, type PlacedFrom, type TaskPlace, type VoiceGuess, type VoicePlace } from "@/lib/pests/voicePlacement";
import { MAX_ACCURACY_M } from "@/lib/pests/gpsPlacement";
import {
  isCrediblyOutsidePerimeter,
  nearestPointOnSegment,
  projectWgs84ToGreenhouseLocal,
  ringFromGeoPolygon,
  type XY,
} from "@/lib/geo/wgs84Local";
import { detectMobileOS, requestLocationPermission } from "@/lib/geo/locationPermission";
import { Button } from "@/components/ui/button";
import {
  FormSheet,
  FormSheetPrimaryButton,
  FormSheetSecondaryButton,
} from "@/components/ui/FormSheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PILL_COLORS } from "@/lib/statusPill";
import { ProcessingState } from "@/components/voice/VoiceStates";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { compressImageForAnalysis, isLikelyImageFile } from "@/lib/imageCompression";
import { adelantarAnalisis, analizarParte, idiomaDelAnalisis } from "@/lib/pests/photoAnalysisRun";
import { VoicePestChoice } from "@/components/worker/VoicePestChoice";
import { clearFileCaptureMark, markFileCaptureStart } from "@/lib/fileCaptureGate";
import { cn } from "@/lib/utils";
import { useFociEnabled } from "@/hooks/useFociEnabled";
import { useConfirmSamplingForm } from "@/hooks/useConfirmSamplingForm";
import { useConfirmScoutingSession } from "@/hooks/useConfirmScoutingSession";
import type { SamplingPlace } from "@/hooks/useFocusLinkProposal";
import { ConfirmSamplingFields } from "@/components/pests/ConfirmSamplingFields";
import { useLineFollowUpCandidate } from "@/hooks/useLineFollowUpCandidate";
import { FollowUpStep } from "@/components/worker/FollowUpStep";
import { FOLLOW_UP_MAX_WAIT_MS, followUpColumns, followUpStage, type FollowUpCandidate, type FollowUpSkipReason, type FollowUpState, type FollowUpVerdict } from "@/lib/pests/followUp";
import type { FollowUpAnswer } from "@/lib/health/focusLinking";
import {
  SHEET_CORNER_OVERRIDE_CLASS,
  VOICE_PANEL_H_CLASS,
  VOICE_PANEL_H_TABLET_CLASS,
} from "@/components/ui/sheetChrome";

type InitialMode = "photo" | "voice";

/** El sitio que un encargado pidió revisar (tarea de seguimiento de un foco). */
export interface FollowUpTaskContext {
  taskId: string;
  focusId: string;
  locationId: string | null;
  greenhouseId: string;
  blockId: string;
  lineNumber: number | null;
  pestName: string;
  lastDate: string | null;
  photoUrl: string | null;
  /** La fila (línea) del foco, si sigue en el diseño. */
  blockRowId?: string | null;
  /** El ancla del foco: «el mismo sitio de la última vez» cuando el GPS no vale (D16). */
  anchor?: { x: number; y: number; t: number | null } | null;
  /** La nota del encargado al pedir la revisión (sin la línea «Línea: N»). */
  note?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMode: InitialMode;
  /** Privileged roles (admin/perito/manager) get an optional pest-classification step. */
  allowClassification?: boolean;
  /** Auto-start recording when opened in voice mode. Off for managers (they tap to start). */
  autoStartVoice?: boolean;
  /**
   * Lo llama el «Atrás» del primer paso. Si se pasa, vuelve al selector de foto/voz de
   * quien nos abrió; si no, el botón cierra el asistente.
   */
  onRequestTabSelector?: () => void;
  /** Fired after a successful submit with the created session id. Only used by
   *  privileged entry points (allowClassification) to offer a post-confirm shortcut. */
  onSubmitted?: (sessionId: string, info?: { followUpDone: boolean }) => void;
  /**
   * El parte nace de una TAREA DE SEGUIMIENTO: el sitio viene puesto y no se
   * cambia, y en vez de «¿es lo mismo?» se pregunta directamente «¿cómo está ahora?».
   */
  followUpTask?: FollowUpTaskContext | null;
  /**
   * El parte se hace DESDE una tarea en marcha («he visto una plaga mientras
   * trataba»): si no dice la nave, es la de la tarea, no la que diga el GPS
   * (Pablo, 24-09-2026: el GPS puso en la Nave 5 un parte de quien trataba la 4).
   */
  taskPlace?: TaskPlace | null;
  /**
   * Fotos que llegan YA hechas desde fuera: el selector de «Reportar plaga» abre la
   * cámara directamente al tocar «Foto», y lo capturado entra aquí al montar. Así el
   * primer paso enseña la foto hecha, no un botón para volver a abrir la cámara.
   */
  initialPhotos?: File[];
  /**
   * "corner": en escritorio la ventana se ancla a la esquina con el mismo alto y
   * ancho que el conmutador de voz. Lo pasa quien abre el asistente DESDE ese
   * conmutador, para que elegir Foto/Voz no salte de la esquina al centro a mitad
   * del flujo. Desde los flujos de operario se abre centrada como siempre.
   */
  anchor?: "center" | "corner";
}

interface CapturedPhoto {
  id: string;
  file: File;
  preview: string;
  /** Ya comprimida (o falló y se queda la original): es el fichero que se subirá. */
  ready?: boolean;
}

const MAX_PHOTOS = 3;
const MAX_AUDIO_SECONDS = 90;
/* Tope de espera del TRABAJADOR mientras se le analiza la nota (D28): pasado
   esto se coloca por GPS y se sigue; el audio se guarda igual, pase lo que pase. */
const VOICE_PREFILL_MAX_MS = 20_000;
/** A partir de aquí una espera ofrece «Saltar» (W6): nadie mira una rueda sin salida. */
const SKIP_AFTER_MS = 5_000;

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* noop */
    }
  }
  return undefined;
}

function mimeToExt(mime: string | undefined): string {
  if (!mime) return "webm";
  if (mime.includes("mp4") || mime.includes("aac")) return "m4a";
  if (mime.includes("webm")) return "webm";
  return "webm";
}

/**
 * Wizard step indexes (severity removed for workers):
 *   0 = capture (photo / voice)
 *   1 = finca (location)
 *   2 = invernadero (greenhouse)
 *   3 = bloque (block)
 *   4 = línea
 *   5 = resumen + confirmar
 */
type SeverityLevel = "low" | "medium" | "high" | "critical";

/** La opción «No lo sé» de la plaga: no es un id de plaga, nunca llega a la base. */
const PEST_UNKNOWN = "__pest_unknown__";

export function IncidentReportWizard({ open, onOpenChange, initialMode, allowClassification = false, autoStartVoice = true, onRequestTabSelector, onSubmitted, followUpTask: followUpTaskProp = null, taskPlace = null, initialPhotos, anchor = "center" }: Props) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { company, user } = useAuth();
  const { allowedLocationIds, excludedBlockIds } = useLocationScope();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(0);

  // Capture state
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioMime, setAudioMime] = useState<string | undefined>(undefined);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [activeCapture, setActiveCapture] = useState<InitialMode>(initialMode);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  /* El segundo camino de la foto: la galería. Input APARTE y no el mismo
     con el `capture` quitado — `capture` es un atributo, no un modo, y
     cambiarlo en caliente antes de cada `.click()` no es fiable en iOS. */
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordTimerRef = useRef<number | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Selection state
  const [locationId, setLocationId] = useState<string | null>(null);
  const [greenhouseId, setGreenhouseId] = useState<string | null>(null);
  const [blockId, setBlockId] = useState<string | null>(null);
  const [lineNumber, setLineNumber] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const didAutoStartRef = useRef(false);

  // GPS OBLIGATORIO (T-B): watch desde que se abre — sin posición no se envía.
  // El bloqueo aplica al WORKER en campo; para admin/perito de escritorio
  // (allowClassification) el GPS se captura si está, pero no bloquea (un
  // permiso denegado en oficina dejaría sin alta de muestreos — QA adversarial).
  const geo = useGeolocation({ enabled: open });
  /* Dónde está según el GPS (nave, sector, línea sugerida): al salir de la captura se coloca
     solo y se va a la línea marcada; las pastillas de arriba dejan cambiarlo (22-09). */
  const gpsGuess = useGpsPlacement(
    geo.position ? { lat: geo.position.lat, lng: geo.position.lng, accuracyM: geo.position.accuracyM } : null,
    allowedLocationIds,
    excludedBlockIds,
    open && !followUpTaskProp,
  );
  /* LO QUE CREEN LA VOZ O EL GPS, SIN ELEGIR POR ÉL (decisión G de Pablo, 23-09-2026):
     el sector y la línea que salen de ahí se marcan en ámbar en su pantalla y se
     confirman con UN toque; antes el GPS dejaba el sector puesto y, con voz, también
     la línea — a ±25 m, con líneas a 1,5–3 m, casi siempre la de al lado.
     La línea va atada al sector en que se sugirió (la del GPS), o a ninguno (la que
     DIJO sin decir sector: vale para el sector que elija, si existe allí — antes se
     borraba al elegir el sector). NO van en el efecto que limpia al cambiar de sitio:
     ese efecto corre justo después de colocar y se las llevaba por delante. */
  const [blockSuggested, setBlockSuggested] = useState<{ greenhouseId: string; blockId: string; from: "voice" | "task" | "gps" } | null>(null);
  const [lineSuggested, setLineSuggested] = useState<{ blockId: string | null; line: number; from: "voice" | "gps" } | null>(null);
  const suggestedBlockHere = blockSuggested && blockSuggested.greenhouseId === greenhouseId ? blockSuggested : null;
  const suggestedLineHere = lineSuggested && (lineSuggested.blockId == null || lineSuggested.blockId === blockId) ? lineSuggested : null;
  /* El sector que DIJO sin decir la nave («línea cuatro del sector A»): se busca por su
     nombre en la nave donde acabe el parte, cuando lleguen sus sectores (efecto de abajo). */
  const [spokenBlock, setSpokenBlock] = useState<string | null>(null);
  const voiceHeardRef = useRef<VoicePlace | null>(null);
  /* Cada análisis de nota lleva su número: si se cierra el asistente (o empieza otro),
     el que llegue tarde no escribe nada en el parte siguiente. */
  const voiceRunRef = useRef(0);
  /* Por qué la voz no dio nada: se le DICE al trabajador en vez de pasar en silencio. */
  const [voiceMiss, setVoiceMiss] = useState<"empty" | "failed" | "skipped" | null>(null);
  /** La plaga dicha encaja en varias (F7, «coincidencia clara o nada»): quien clasifica elige. */
  const [voicePestChoices, setVoicePestChoices] = useState<{ spoken: string; options: Array<{ id: string; name: string }> } | null>(null);
  /* El id del parte se fija UNA vez por apertura: si la subida de una foto falla y
     vuelve a enviar, no nace un segundo parte (antes quedaba uno sin fotos). */
  const pendingSessionIdRef = useRef<string>(crypto.randomUUID());
  /* La adivinanza más reciente, para leerla desde una promesa que arrancó antes. */
  const gpsGuessRef = useRef<GpsPlacement | null>(null);
  gpsGuessRef.current = gpsGuess.placement;
  const gpsBlocking = geo.isBlocking && !allowClassification;

  // Optional pest classification — only surfaced for privileged reporters.
  const [classifyPestId, setClassifyPestId] = useState<string | null>(null);
  /* «No lo sé» en la plaga (Pablo, 25-09-2026: «no tienes que poner plaga sí o sí: igual no
     tienes ni idea»). Sin plaga el parte entra SIN clasificar —pendiente en el triaje, sin
     foco—; esto solo lo dice en voz alta, y deja quitar una plaga elegida por error. */
  const [pestUnknown, setPestUnknown] = useState(false);
  const { data: classifyPests = [] } = useQuery({
    queryKey: ["incident-classify-pests", company?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pests")
        .select("id, name, translation_key")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; name: string; translation_key?: string | null }>;
    },
    enabled: !!company?.id && open && allowClassification,
  });

  // Voice-prefill (managers/admins only). 'capture' = normal recording UI,
  // 'interpreting' = transcribing/extracting spinner, 'preview' = confirm what
  // the AI understood before continuing. Never used on the worker path.
  const [severityLevel, setSeverityLevel] = useState<SeverityLevel | null>(null);
  const [voicePhase, setVoicePhase] = useState<"capture" | "interpreting" | "preview">("capture");
  const [transcript, setTranscript] = useState<string>("");
  /* Lo que el paso de voz YA dejó hecho: la URL definitiva del audio en
     almacenamiento y el idioma detectado. Sin esto, el mismo audio se subía dos
     veces (aquí y en la función de transcripción, a dos cubos distintos) y el
     texto se tiraba, así que al abrir la ficha había que transcribirlo otra vez.
     Vacíos en el camino manual, donde todavía no se ha transcrito nada. */
  const [storedAudioUrl, setStoredAudioUrl] = useState<string | null>(null);
  const [transcriptLang, setTranscriptLang] = useState<string | null>(null);
  const [voiceWarning, setVoiceWarning] = useState<any | null>(null);
  /* La voz del TRABAJADOR (D28): la plaga que entendió viaja como sugerencia para
     quien clasifica (`voice_guess`), y se recuerda de dónde salió cada nivel del
     sitio (voz o GPS) para decírselo. `voiceTriedRef`: una nota se analiza UNA vez;
     si no se entendió, volver a «Continuar» no vuelve a pagar la llamada. */
  const [voiceGuess, setVoiceGuess] = useState<VoiceGuess | null>(null);
  const [voicePlacedBy, setVoicePlacedBy] = useState<PlacedFrom | null>(null);
  const voiceTriedRef = useRef(false);
  /* «Saltar» mientras se analiza la nota (W6): aborta la llamada en curso. */
  const voiceAbortRef = useRef<AbortController | null>(null);
  const voiceSkippedRef = useRef(false);
  const [canSkipVoice, setCanSkipVoice] = useState(false);
  useEffect(() => {
    if (voicePhase !== "interpreting") {
      setCanSkipVoice(false);
      return;
    }
    const timer = window.setTimeout(() => setCanSkipVoice(true), SKIP_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [voicePhase]);

  /* La pregunta al trabajador (focos): lo que contestó. `followSkipped` = ya se
     decidió no preguntar en este parte (no había nada, o tardó demasiado). */
  const [followAnswer, setFollowAnswer] = useState<FollowUpAnswer | null>(null);
  /* Con varios sitios avisados en la línea, el que eligió entre fotos (D24). */
  const [followChosen, setFollowChosen] = useState<FollowUpCandidate | null>(null);
  const [followVerdict, setFollowVerdict] = useState<FollowUpVerdict | null>(null);
  const [followSkipped, setFollowSkipped] = useState(false);
  /* Por qué NO se pudo preguntar (D20): viaja con el parte para que quien confirma lo sepa. */
  const [followSkipReason, setFollowSkipReason] = useState<FollowUpSkipReason | null>(null);
  /* «NO ESTOY EN ESE SITIO». El parte venía de una tarea de seguimiento, pero quien lo hace dice
     que no está allí: deja de ser la revisión que se pidió. Se suelta el sitio para que elija el
     suyo, no viaja ninguna pista, y la tarea NO se da por hecha. */
  const [followDetached, setFollowDetached] = useState(false);
  const followUpTask = followDetached ? null : followUpTaskProp;

  // Reset everything when reopened
  useEffect(() => {
    if (open) {
      setStep(0);
      setFollowAnswer(null); setFollowChosen(null);
      setBlockSuggested(null); setLineSuggested(null); setSpokenBlock(null); setVoiceMiss(null); setVoicePestChoices(null);
      voiceHeardRef.current = null;
      pendingSessionIdRef.current = crypto.randomUUID();
      setFollowVerdict(null);
      setFollowSkipped(false);
      setFollowDetached(false);
      // Desde una tarea de seguimiento el sitio viene puesto: es ESE sitio el que se pidió mirar.
      if (followUpTaskProp) {
        setLocationId(followUpTaskProp.locationId);
        setGreenhouseId(followUpTaskProp.greenhouseId);
        setBlockId(followUpTaskProp.blockId);
        setLineNumber(followUpTaskProp.lineNumber);
      }
      setActiveCapture(initialMode);
      didAutoStartRef.current = false;
      setClassifyPestId(null);
      setPestUnknown(false);
      setSeverityLevel(null);
      setVoicePhase("capture");
      setTranscript("");
      setTranscriptLang(null);
      setStoredAudioUrl(null);
      setVoiceWarning(null);
      setVoiceGuess(null);
      setVoicePlacedBy(null);
      voiceTriedRef.current = false;
    } else {
      // Cleanup on close
      // Un análisis de nota a medias no puede escribir en el parte siguiente.
      voiceRunRef.current++;
      voiceAbortRef.current?.abort();
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setPhotos([]);
      setAudioBlob(null);
      setAudioUrl(null);
      setAudioMime(undefined);
      setRecordSeconds(0);
      setLocationId(null);
      setGreenhouseId(null);
      setBlockId(null);
      setLineNumber(null);
      setSeverityLevel(null);
      setVoicePhase("capture");
      setTranscript("");
      setTranscriptLang(null);
      setStoredAudioUrl(null);
      setVoiceWarning(null);
      setVoiceGuess(null);
      setVoicePlacedBy(null);
      voiceTriedRef.current = false;
      setSubmitting(false);
      if (recordTimerRef.current) {
        window.clearInterval(recordTimerRef.current);
        recordTimerRef.current = null;
      }
      streamRef.current?.getTracks().forEach((tk) => tk.stop());
      streamRef.current = null;
      setRecording(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ---------------- Data queries ---------------- */

  /* MUESTREO: SOLO SE PUEDE DAR PARTE DE LO QUE SE LLEVA (02-09-2026).
     Esto crea un `pest_scouting_session`, o sea que ESCRIBE, y el desplegable
     ofrecía las fincas de toda la empresa: un encargado de Las Norias podía
     apuntar una plaga en Campohermoso, que además dispara aviso y acaba en el
     mapa y en los informes de otro. Igual que en la tarea por voz, lo que lo
     impide de verdad es la política de INSERT de la base de datos; esto es para
     que el desplegable no ofrezca lo que luego se va a rechazar. */
  const { data: locations = [] } = useQuery({
    queryKey: ["worker-incident-locations", company?.id, claveAlcance(allowedLocationIds)],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("id, name")
        .order("name");
      if (error) throw error;
      const todas = data ?? [];
      return allowedLocationIds ? todas.filter((l) => allowedLocationIds.has(l.id)) : todas;
    },
    enabled: !!company?.id && open,
  });

  const { data: greenhouses = [] } = useQuery({
    queryKey: ["worker-incident-greenhouses", company?.id, locationId],
    queryFn: async () => {
      if (!locationId) return [];
      const { data, error } = await supabase
        .from("greenhouses")
        .select("id, name, location_id")
        .eq("location_id", locationId)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!company?.id && !!locationId && open,
  });

  const { data: blocks = [], isFetching: blocksFetching } = useQuery({
    queryKey: ["worker-incident-blocks", company?.id, greenhouseId, claveAlcance(null, excludedBlockIds)],
    queryFn: async () => {
      if (!greenhouseId) return [];
      const { data, error } = await supabase
        .from("blocks")
        .select("id, name, row_start, row_end, geometry")
        .eq("greenhouse_id", greenhouseId)
        .is("archived_at", null)
        .order("name");
      if (error) throw error;
      const todos = data ?? [];
      return excludedBlockIds?.size ? todos.filter((b) => !excludedBlockIds.has(b.id)) : todos;
    },
    enabled: !!company?.id && !!greenhouseId && open,
  });

  const activeBlock = useMemo(
    () => blocks.find((b) => b.id === blockId) ?? null,
    [blocks, blockId],
  );

  // Líneas REALES del bloque (post-backfill casi todos las tienen): dan el
  // block_row_id y la geometría del segmento para el anclaje GPS×línea.
  const { data: blockRows = [], isFetching: blockRowsFetching } = useQuery({
    queryKey: ["worker-incident-block-rows", blockId],
    queryFn: async () => {
      if (!blockId) return [];
      const { data, error } = await supabase
        .from("block_rows")
        .select("id, line_number, geometry")
        .eq("block_id", blockId)
        .eq("is_active", true)
        .order("line_number");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!blockId && open,
  });

  // Geometría de la nave declarada: para el anclaje GPS×línea del submit y
  // para el aviso de perímetro en vivo (se pide al elegir nave, no al enviar).
  const { data: ghGeo = null } = useQuery({
    queryKey: ["worker-incident-gh-geo", greenhouseId],
    queryFn: async () => {
      if (!greenhouseId) return null;
      const { data, error } = await supabase
        .from("greenhouses")
        .select("geometry, centroid_lat, centroid_lng")
        .eq("id", greenhouseId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
    enabled: !!greenhouseId && open,
    staleTime: 5 * 60_000,
  });

  // GPS proyectado al plano local de la nave (null sin fix o sin geometría).
  const gpsLocal = useMemo<XY | null>(() => {
    if (!geo.position || !ghGeo || ghGeo.centroid_lat == null || ghGeo.centroid_lng == null) {
      return null;
    }
    const ring = ringFromGeoPolygon(
      (ghGeo.geometry as { geoPolygonWgs84?: unknown } | null)?.geoPolygonWgs84,
    );
    if (ring.length < 3) return null;
    return projectWgs84ToGreenhouseLocal(
      { lat: geo.position.lat, lng: geo.position.lng },
      { centroid: { lat: ghGeo.centroid_lat, lng: ghGeo.centroid_lng }, ringWgs84: ring },
    );
  }, [geo.position, ghGeo]);

  // ¿El GPS sitúa al trabajador CREÍBLEMENTE lejos de la nave declarada?
  // (regla de Tactic: distancia − min(precisión,50) > 100). NUNCA bloquea el
  // envío (decisión usuario 2026-08-04): solo avisa y degrada el anclaje a
  // punto medio de línea — el along-track de un GPS lejano sería basura.
  const farFromGreenhouse = useMemo(() => {
    if (!gpsLocal || !ghGeo) return false;
    const perimeter =
      (ghGeo.geometry as { perimeter?: XY[] } | null)?.perimeter ?? [];
    return isCrediblyOutsidePerimeter(gpsLocal, perimeter, geo.position?.accuracyM ?? null);
  }, [gpsLocal, ghGeo, geo.position?.accuracyM]);

  /* EL GPS DICE OTRO SITIO (Pablo, 24-09-2026: «que te avise que en la nave que ha
     reportado el GPS no coincide algo; que te avise y ya está»). Contra lo que el
     parte tiene puesto AHORA —lo dijo, es su tarea o lo eligió—: otra nave, o la misma
     nave y otro sector. Nunca cambia nada solo: avisa, y un botón lleva a lo del GPS. */
  const gpsMismatch = useMemo<GpsMismatch | null>(
    () =>
      followUpTask
        ? null
        : gpsMismatchOf({ greenhouseId, blockId: blockId ?? (blockSuggested?.greenhouseId === greenhouseId ? blockSuggested?.blockId ?? null : null) }, gpsGuess.placement),
    [followUpTask, greenhouseId, blockId, blockSuggested, gpsGuess.placement],
  );
  const { data: gpsMismatchNames = null } = useQuery({
    queryKey: ["worker-incident-gps-names", gpsMismatch?.gpsGreenhouseId, gpsMismatch?.gpsBlockId],
    queryFn: async () => {
      const [gh, bl] = await Promise.all([
        supabase.from("greenhouses").select("name, location_id, location:locations(name)").eq("id", gpsMismatch!.gpsGreenhouseId).maybeSingle(),
        gpsMismatch!.gpsBlockId
          ? supabase.from("blocks").select("name").eq("id", gpsMismatch!.gpsBlockId).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      const g = gh.data as { name?: string; location_id?: string | null; location?: { name?: string } | null } | null;
      return {
        greenhouse: g?.name ?? null,
        locationId: g?.location_id ?? null,
        location: g?.location?.name ?? null,
        block: (bl.data as { name?: string } | null)?.name ?? null,
      };
    },
    enabled: !!gpsMismatch && open,
    staleTime: 5 * 60_000,
  });

  const lineOptions = useMemo<number[]>(() => {
    if (blockRows.length > 0) return blockRows.map((r) => r.line_number);
    if (!activeBlock) return [];
    const s = activeBlock.row_start ?? null;
    const e = activeBlock.row_end ?? null;
    if (s == null || e == null || e < s) return [];
    const list: number[] = [];
    for (let i = s; i <= e; i += 1) list.push(i);
    return list;
  }, [activeBlock, blockRows]);

  /* Elegir el sector NO borra la línea que ya estaba —la que DIJO en la nota
     («araña roja, grave, la línea 10 de chillida», sin sector), o la que eligió
     antes— si ese sector la tiene (Pablo, 24-09-2026: al poner el sector A se
     quitaba la línea). Solo se sabe cuando llegan las líneas del sector nuevo:
     entonces, si no está, se quita. Únicamente tras elegir sector a mano; la
     revisión pedida llega con el sitio puesto y no pasa por aquí. */
  const [lineCheckBlock, setLineCheckBlock] = useState<string | null>(null);
  useEffect(() => {
    if (!lineCheckBlock || lineCheckBlock !== blockId || blockRowsFetching) return;
    if (lineNumber != null && !lineOptions.includes(lineNumber)) {
      setLineNumber(null);
      // Estaba puesta sola (voz o GPS) y ya en el resumen: a elegirla, no a un «falta la línea».
      if (!allowClassification) setStep((s) => (s === 5 ? 4 : s));
    }
    setLineCheckBlock(null);
  }, [lineCheckBlock, blockId, blockRowsFetching, lineOptions, lineNumber, allowClassification]);

  /* ── FOCOS (solo con el interruptor encendido y eligiendo plaga) ─────────────
     Quien puede clasificar aquí CONFIRMA: plaga + gravedad + a qué foco pertenece.
     El sitio se calcula igual que al enviar —la línea DECLARADA manda y el GPS solo
     afina dentro de ella— para que lo que se propone sea lo que se va a guardar.
     Sin plaga elegida, o si los focos no se pueden leer, el parte sale como
     siempre: capturar no se rompe por esto. */
  const { enabled: fociEnabled } = useFociEnabled();
  const fociPlace = useMemo<SamplingPlace | null>(() => {
    if (!fociEnabled || !allowClassification || !blockId) return null;
    const row = lineNumber != null ? blockRows.find((r) => r.line_number === lineNumber) ?? null : null;
    const seg = row?.geometry as { a?: XY; b?: XY } | null;
    // Solo un GPS creíble (≤ 25 m) cuenta como posición MEDIDA; con ±999 era basura presentada como exacta.
    const credible = (geo.position?.accuracyM ?? Number.POSITIVE_INFINITY) <= MAX_ACCURACY_M;
    const snap = seg?.a && seg?.b && gpsLocal && !farFromGreenhouse && credible ? nearestPointOnSegment(gpsLocal, seg.a, seg.b) : null;
    return {
      sessionId: null,
      blockId,
      rowId: row?.id ?? null,
      lineNumber,
      anchorT: snap ? snap.t : null,
      anchorSource: snap ? "gps_snap" : null,
      accuracyM: geo.position?.accuracyM ?? null,
      date: new Date().toISOString().slice(0, 10),
      hint: null,
    };
  }, [fociEnabled, allowClassification, blockId, lineNumber, blockRows, gpsLocal, farFromGreenhouse, geo.position?.accuracyM]);
  const fociWanted = fociEnabled && allowClassification && open && !!classifyPestId && !!blockId;
  const fociForm = useConfirmSamplingForm({
    enabled: fociWanted,
    place: fociPlace,
    pestId: classifyPestId,
    pestName: classifyPests.find((p) => p.id === classifyPestId)?.name ?? null,
    controlledSeverity: severityLevel,
  });
  const { confirm: confirmSampling } = useConfirmScoutingSession();
  /** Los focos cuentan para ESTE envío. Si no se pudieron leer, el parte sale por el camino de siempre. */
  const fociActive = fociWanted && !fociForm.loadError;
  const fociBlocked = fociActive && fociForm.blocked !== null;

  /* ── LA PREGUNTA AL TRABAJADOR (solo su camino, solo con focos encendidos) ────
     «Hace 5 días se avisó de MOSCA BLANCA en esta línea. ¿Es lo mismo que ves
     ahora?» Los focos del sector se piden en cuanto se elige —no al llegar a la
     pregunta— y si aun así tardan, no se pregunta: capturar no espera por esto.
     Quien puede clasificar NO pasa por aquí: ese confirma con el campo «Foco». */
  const followEnabled = fociEnabled && !allowClassification && open;
  /* La nota del TRABAJADOR también se analiza al capturar (D28): sitio por lo que
     dice + GPS, y la plaga como sugerencia. Solo en el piloto de focos, que es
     donde se decide si esto acierta (cuesta una transcripción y una llamada). */
  const workerVoice = fociEnabled && !allowClassification;
  const questionPlace = useMemo(() => {
    if (!followEnabled || !blockId) return null;
    const row = lineNumber != null ? blockRows.find((r) => r.line_number === lineNumber) ?? null : null;
    return { blockId, rowId: row?.id ?? null, lineNumber, date: new Date().toISOString().slice(0, 10) };
  }, [followEnabled, blockId, lineNumber, blockRows]);
  const followUp = useLineFollowUpCandidate(questionPlace, followEnabled);
  /* Desde una tarea de seguimiento se pregunta por EL FOCO DE LA TAREA, esté o no
     en la línea exacta: el encargado ya eligió de qué sitio quiere saber. */
  const followCandidate = useMemo(() => {
    if (!followUpTask) return followChosen ?? followUp.candidate;
    const today = new Date().toISOString().slice(0, 10);
    const lastDate = followUpTask.lastDate ?? today;
    return {
      focusId: followUpTask.focusId,
      pestName: followUpTask.pestName,
      lastDate,
      daysAgo: 0,
      photoUrl: followUpTask.photoUrl,
    };
  }, [followUpTask, followUp.candidate, followChosen]);
  const followState: FollowUpState = {
    enabled: followEnabled,
    status: followUpTask ? "ready" : followUp.status,
    candidate: followCandidate,
    skipped: followSkipped,
    skipReason: followSkipReason,
    answer: followAnswer,
    verdict: followVerdict,
    fromTask: !!followUpTask,
  };
  const followStage = step === 5 ? followUpStage(followState) : "off";
  const followAsking = followStage === "ask_same" || followStage === "ask_how" || followStage === "waiting";
  /* A los 3 s de esperar la pregunta, «Saltar» (W6): hasta 8 s de rueda muda era mucho. */
  const [canSkipFollow, setCanSkipFollow] = useState(false);
  useEffect(() => {
    if (followStage !== "waiting") {
      setCanSkipFollow(false);
      return;
    }
    const timer = window.setTimeout(() => setCanSkipFollow(true), 3_000);
    return () => window.clearTimeout(timer);
  }, [followStage]);

  // Otro sitio, otra pregunta: lo contestado para la línea anterior ya no vale.
  useEffect(() => {
    setFollowAnswer(null); setFollowChosen(null);
    setFollowVerdict(null);
    setFollowSkipped(false);
    setFollowSkipReason(null);
  }, [blockId, lineNumber]);

  /* Al llegar al resumen se DECIDE, y ya no se cambia: si no hay nada que
     preguntar se enseña el resumen y se queda; si los focos aún se están pidiendo
     se espera un momento y, si no llegan, se sigue sin preguntar. Una pantalla que
     cambia sola mientras alguien la lee es peor que una pregunta de menos. */
  useEffect(() => {
    if (step !== 5 || !followEnabled || followSkipped) return;
    if (followStage === "off") {
      setFollowSkipped(true);
      // «Nada que preguntar» no es un motivo; «no se pudo preguntar» sí (D20).
      if (!followUpTask && followUp.status === "failed") setFollowSkipReason(typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "error");
      return;
    }
    if (followStage !== "waiting") return;
    const timer = window.setTimeout(() => { setFollowSkipped(true); setFollowSkipReason("timeout"); }, FOLLOW_UP_MAX_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [step, followEnabled, followSkipped, followStage]);

  // Auto-skip Finca step when only one location exists.
  useEffect(() => {
    if (open && step === 1 && !locationId && locations.length === 1) {
      setLocationId(locations[0].id);
      setStep(2);
    }
  }, [open, step, locationId, locations]);

  // Auto-skip Línea step when block has no line range configured.
  useEffect(() => {
    if (open && step === 4 && activeBlock && lineOptions.length === 0) {
      setStep(5);
    }
  }, [open, step, activeBlock, lineOptions.length]);

  /* ---------------- Photo capture ---------------- */

  /**
   * Mete UNA foto, sin mirar el tope.
   *
   * El tope lo comprueba quien llama, y esa separación no es un capricho: la
   * comprobación necesita saber cuántas van YA, y `photos.length` aquí dentro es
   * el del render en el que se creó la función. Con una sola foto (la cámara) da
   * igual; con tres de golpe desde la galería, las tres leían el mismo número,
   * las tres pasaban el filtro y acababas con más de las que caben.
   */
  const addPhoto = useCallback(
    async (file: File) => {
      if (!isLikelyImageFile(file)) {
        toast({ title: t("worker.incident.photoInvalid"), variant: "destructive" });
        return;
      }
      const id = crypto.randomUUID();
      const rawUrl = URL.createObjectURL(file);
      setPhotos((prev) => [...prev, { id, file, preview: rawUrl }]);
      try {
        const { file: compressed } = await compressImageForAnalysis(file);
        const cUrl = URL.createObjectURL(compressed);
        setPhotos((prev) =>
          prev.map((p) => {
            if (p.id !== id) return p;
            URL.revokeObjectURL(p.preview);
            return { id, file: compressed, preview: cUrl, ready: true };
          }),
        );
      } catch (err) {
        console.error("Photo compression failed:", err);
        setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, ready: true } : p)));
      }
    },
    [t, toast],
  );

  /**
   * Varias fotos de golpe (galería), respetando el tope UNA sola vez.
   *
   * Se recorta a lo que queda y se avisa solo si sobraba algo: elegir cinco y
   * ver que entran tres sin que nadie lo diga parece que la app se ha comido
   * dos.
   */
  const handlePhotoFiles = useCallback(
    (files: File[]) => {
      const room = MAX_PHOTOS - photos.length;
      if (room <= 0) {
        toast({ title: t("worker.incident.maxPhotos", { max: MAX_PHOTOS }) });
        return;
      }
      if (files.length > room) {
        toast({ title: t("worker.incident.maxPhotos", { max: MAX_PHOTOS }) });
      }
      for (const f of files.slice(0, room)) void addPhoto(f);
    },
    [addPhoto, photos.length, t, toast],
  );

  const onPhotoInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    clearFileCaptureMark();
    if (files.length === 0) return; // cámara/galería cancelada
    handlePhotoFiles(files);
  };

  // Fotos hechas ANTES de abrir (la cámara que dispara el propio selector de
  // «Reportar plaga»): entran al abrir, una vez por apertura.
  const ingestedInitialRef = useRef(false);
  useEffect(() => {
    if (!open) {
      ingestedInitialRef.current = false;
      return;
    }
    if (ingestedInitialRef.current) return;
    ingestedInitialRef.current = true;
    if (initialPhotos && initialPhotos.length > 0) {
      handlePhotoFiles(initialPhotos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openCamera = () => {
    if (photos.length >= MAX_PHOTOS) {
      toast({ title: t("worker.incident.maxPhotos", { max: MAX_PHOTOS }) });
      return;
    }
    markFileCaptureStart();
    cameraInputRef.current?.click();
  };

  /* Lo mismo, por la galería. El `.click()` cuelga del gesto que lo pidió: en
     otro tick, Safari no abre nada. */
  const openGallery = () => {
    if (photos.length >= MAX_PHOTOS) {
      toast({ title: t("worker.incident.maxPhotos", { max: MAX_PHOTOS }) });
      return;
    }
    markFileCaptureStart();
    galleryInputRef.current?.click();
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  /* ---------------- Audio capture ---------------- */

  const stopRecording = useCallback(() => {
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      /* noop */
    }
    if (recordTimerRef.current) {
      window.clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((tk) => tk.stop());
    streamRef.current = null;
    setRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickRecorderMime();
      setAudioMime(mime);
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recordChunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(recordChunksRef.current, { type });
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = window.setInterval(() => {
        setRecordSeconds((s) => {
          const next = s + 1;
          if (next >= MAX_AUDIO_SECONDS) {
            stopRecording();
          }
          return next;
        });
      }, 1000);
      return true;
    } catch (err) {
      console.error("Mic permission error:", err);
      toast({ title: t("worker.incident.micPermissionError"), variant: "destructive" });
      // Devuelve si se está grabando de verdad: quien llame necesita saberlo
      // para no dejar al usuario mirando una grabadora que nunca arrancó.
      return false;
    }
  }, [audioUrl, stopRecording, t, toast]);

  const removeAudio = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioMime(undefined);
    setRecordSeconds(0);
    // Otra grabación es otro audio: lo ya subido y transcrito no vale.
    setStoredAudioUrl(null);
    setTranscript("");
    setTranscriptLang(null);
    setVoiceGuess(null);
    setVoicePlacedBy(null);
    // Y lo que esa nota sugirió (sector, línea, sector dicho, «no te he entendido») tampoco.
    setBlockSuggested(null);
    setLineSuggested(null);
    setSpokenBlock(null);
    setVoiceMiss(null);
    setVoicePestChoices(null);
    voiceHeardRef.current = null;
    voiceTriedRef.current = false;
  };

  // Voice mode → auto-start recording when opened on capture step (workers only;
  // managers tap the mic to start, so they aren't recording before they're ready).
  useEffect(() => {
    if (!open) return;
    if (didAutoStartRef.current) return;
    if (autoStartVoice && initialMode === "voice" && step === 0 && !audioBlob && !recording) {
      didAutoStartRef.current = true;
      void startRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMode, step]);

  /* ANÁLISIS ADELANTADO (Pablo, 28-09-2026: «que se analice mientras lo mandas»). En
     cuanto las fotos están listas —comprimidas, que es lo que se sube— se pide ya el
     análisis, sin esperar a enviar. Con el sector puesto va enseguida (la IA lee su
     cultivo y su tiempo); sin él, se le dan unos segundos a la voz o al GPS para ponerlo.
     Mismas fotos, misma petición: cambiar de pantalla no la repite. */
  useEffect(() => {
    if (!open || recording || photos.length === 0 || photos.some((p) => !p.ready)) return;
    const parteId = pendingSessionIdRef.current;
    const files = photos.map((p) => p.file);
    const espera = window.setTimeout(
      () => adelantarAnalisis(parteId, files, { lang: idiomaDelAnalisis(i18n.language), blockId }),
      blockId ? 300 : 8000,
    );
    return () => window.clearTimeout(espera);
  }, [open, recording, photos, blockId, i18n.language]);

  /* ---------------- Submission ---------------- */

  const uploadPhotos = async (sessionId: string): Promise<string[]> => {
    const urls: string[] = [];
    const companyId = company?.id;
    if (!companyId) throw new Error("missing company");
    for (let i = 0; i < photos.length; i += 1) {
      const p = photos[i];
      const ext = (p.file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${companyId}/${sessionId}/photo-${i + 1}.${ext}`;
      const { error } = await supabase.storage
        .from("scouting-photos")
        .upload(path, p.file, { upsert: true, contentType: p.file.type || "image/jpeg" });
      if (error) throw error;
      const { data } = supabase.storage.from("scouting-photos").getPublicUrl(path);
      if (data?.publicUrl) urls.push(data.publicUrl);
    }
    return urls;
  };

  const uploadAudio = async (sessionId: string): Promise<string | null> => {
    if (!audioBlob) return null;
    /* Si el paso de voz ya lo subió al transcribir, se reutiliza esa copia. Antes
       se subía otra vez, a otro cubo, y quedaban dos ficheros idénticos. */
    if (storedAudioUrl) return storedAudioUrl;
    const companyId = company?.id;
    if (!companyId) throw new Error("missing company");
    const ext = mimeToExt(audioMime ?? audioBlob.type);
    const path = `${companyId}/${sessionId}/audio.${ext}`;
    const { error } = await supabase.storage
      .from("scouting-photos")
      .upload(path, audioBlob, { upsert: true, contentType: audioBlob.type || "audio/webm" });
    if (error) throw error;
    const { data } = supabase.storage.from("scouting-photos").getPublicUrl(path);
    return data?.publicUrl ?? null;
  };

  const handleSubmit = async () => {
    if (!company?.id || !user?.id) {
      toast({ title: t("common.error"), variant: "destructive" });
      return;
    }
    if (!greenhouseId || !blockId) {
      toast({ title: t("worker.incident.fillRequired"), variant: "destructive" });
      return;
    }
    if (photos.length === 0 && !audioBlob) {
      toast({ title: t("worker.incident.captureRequired"), variant: "destructive" });
      return;
    }
    if (lineOptions.length > 0 && lineNumber == null) {
      toast({ title: t("worker.incident.lineRequired"), variant: "destructive" });
      return;
    }
    if (gpsBlocking) {
      toast({ title: t("worker.incident.gpsRequired"), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const ghName = greenhouses.find((g) => g.id === greenhouseId)?.name ?? null;
      const blockName = activeBlock?.name ?? null;
      const today = new Date().toISOString().slice(0, 10);

      // Anclaje GPS×línea (T-B): la línea DECLARADA manda; el GPS solo afina
      // la altura dentro de ella (punto más cercano del segmento). Sin línea
      // (bloque sin filas) → centroide del bloque, marcado aproximado.
      const declaredRow =
        lineNumber != null ? blockRows.find((r) => r.line_number === lineNumber) ?? null : null;
      const rowSeg = declaredRow?.geometry as
        | { a?: { x: number; y: number }; b?: { x: number; y: number } }
        | null;
      // Geometría FRESCA en el instante del envío (adversarial 2026-08-04):
      // el prefetch (ghGeo) alimenta el banner en vivo, pero anclar con una
      // caché de hasta 5 min arriesga proyectar en el marco viejo tras un
      // republicado (el origen local es el mín del anillo: si el perímetro
      // cambia, TODO el marco se mueve), y una query aún cargando degradaba
      // el ancla en silencio. El coste es un select diminuto y para insertar
      // hay red de todos modos.
      let freshLocal: XY | null = null;
      let freshFar = false;
      if (geo.position) {
        const { data: freshGh } = await supabase
          .from("greenhouses")
          .select("geometry, centroid_lat, centroid_lng")
          .eq("id", greenhouseId)
          .maybeSingle();
        const ring = ringFromGeoPolygon(
          (freshGh?.geometry as { geoPolygonWgs84?: unknown } | null)?.geoPolygonWgs84,
        );
        if (ring.length >= 3 && freshGh?.centroid_lat != null && freshGh?.centroid_lng != null) {
          freshLocal = projectWgs84ToGreenhouseLocal(
            { lat: geo.position.lat, lng: geo.position.lng },
            { centroid: { lat: freshGh.centroid_lat, lng: freshGh.centroid_lng }, ringWgs84: ring },
          );
          const perimeter =
            (freshGh?.geometry as { perimeter?: XY[] } | null)?.perimeter ?? [];
          if (freshLocal) {
            freshFar = isCrediblyOutsidePerimeter(freshLocal, perimeter, geo.position.accuracyM);
          }
        }
      }

      let anchor: { x: number; y: number; t: number | null; source: string } | null = null;
      if (rowSeg?.a && rowSeg?.b) {
        // Lejos de la nave el along-track del GPS es basura: punto medio de
        // la línea declarada, con source PROPIO para que quede rastro
        // persistente de la degradación (el mapa lo pinta aproximado:
        // cualquier source ≠ gps_snap lo es).
        // Solo con un GPS CREÍBLE (≤ 25 m, el mismo tope que la colocación por GPS)
        // la posición es medida: con la precisión de ±999 que a veces declara Despia
        // se guardaba como exacta mientras la pantalla decía «aproximado» (W4).
        const credible = (geo.position?.accuracyM ?? Number.POSITIVE_INFINITY) <= MAX_ACCURACY_M;
        if (freshLocal && !freshFar && credible) {
          const near = nearestPointOnSegment(freshLocal, rowSeg.a, rowSeg.b);
          anchor = { x: near.point.x, y: near.point.y, t: near.t, source: "gps_snap" };
        } else if (freshLocal && freshFar) {
          anchor = {
            x: (rowSeg.a.x + rowSeg.b.x) / 2,
            y: (rowSeg.a.y + rowSeg.b.y) / 2,
            t: 0.5,
            source: "line_midpoint_gps_far",
          };
        }
        if (!anchor) {
          anchor = {
            x: (rowSeg.a.x + rowSeg.b.x) / 2,
            y: (rowSeg.a.y + rowSeg.b.y) / 2,
            t: 0.5,
            source: "line_midpoint",
          };
        }
      } else {
        const pts =
          (activeBlock?.geometry as { points?: { x: number; y: number }[] } | null)?.points ?? [];
        if (pts.length >= 3) {
          anchor = {
            x: pts.reduce((acc, pt) => acc + pt.x, 0) / pts.length,
            y: pts.reduce((acc, pt) => acc + pt.y, 0) / pts.length,
            t: null,
            source: "block_centroid",
          };
        }
      }

      const sessionPayload: Record<string, unknown> = {
        company_id: company.id,
        inspector_id: user.id,
        date: today,
        greenhouse_id: greenhouseId,
        block_id: blockId,
        greenhouse_name_snapshot: ghName,
        block_name_snapshot: blockName,
        source: "worker_quick_report",
        review_status: "pending",
        notes: null,
        block_row_id: declaredRow?.id ?? null,
        gps_lat: geo.position?.lat ?? null,
        gps_lng: geo.position?.lng ?? null,
        gps_accuracy_m: geo.position?.accuracyM ?? null,
        anchor_x: anchor ? Math.round(anchor.x * 100) / 100 : null,
        anchor_y: anchor ? Math.round(anchor.y * 100) / 100 : null,
        anchor_t: anchor?.t ?? null,
        anchor_source: anchor?.source ?? null,
      };
      /* CON FOCOS el parte entra PENDIENTE y se confirma después, por la única
         puerta (`useConfirmScoutingSession`), con su gravedad y su foco. Si esa
         llamada falla el parte ya está guardado y se confirma luego desde su ficha. */
      const viaFoci = fociActive && !fociBlocked;
      // Lo que contestó el trabajador viaja con el parte como PISTA (solo con focos encendidos).
      Object.assign(sessionPayload, followUpColumns(followState, followUpTask?.taskId ?? null));
      // Y lo que entendió la voz del trabajador, como SUGERENCIA (D28): nunca clasifica.
      if (!allowClassification && voiceGuess) sessionPayload.voice_guess = voiceGuess;
      // Privileged reporters (admin/perito/manager) may pre-classify the pest.
      if (allowClassification && classifyPestId && !viaFoci) {
        const pest = classifyPests.find((p) => p.id === classifyPestId);
        sessionPayload.triage_status = "classified";
        sessionPayload.triage_pest_id = classifyPestId;
        sessionPayload.triage_pest_name_snapshot = pest?.name ?? null;
        sessionPayload.triaged_by = user.id;
        sessionPayload.triaged_at = new Date().toISOString();
      }
      // Severity spoken in the voice note (managers/admins only). Column CHECK
      // allows low/medium/high/critical or NULL.
      if (allowClassification && severityLevel) {
        sessionPayload.severity = severityLevel;
      }

      // Id fijado al abrir: si una foto no sube y vuelve a enviar, es EL MISMO parte (W7).
      const sessionId = pendingSessionIdRef.current;
      sessionPayload.id = sessionId;
      const { error: insertError } = await supabase.from("pest_scouting_sessions").insert(sessionPayload as any);
      if (insertError) {
        // 23505 = el intento anterior YA lo insertó: se sigue con ese parte. Se comprueba que
        // existe (un 23505 de otra cosa no es «ya estaba») y se le pone el sitio de ahora, por si
        // lo cambió entre intentos. Solo columnas de sitio: las pistas no las puede tocar el cliente.
        if ((insertError as { code?: string }).code !== "23505") throw insertError;
        const { data: ya } = await supabase.from("pest_scouting_sessions").select("id").eq("id", sessionId).maybeSingle();
        if (!ya) throw insertError;
        const sitio: Record<string, unknown> = {};
        for (const k of ["greenhouse_id", "block_id", "block_row_id", "greenhouse_name_snapshot", "block_name_snapshot", "gps_lat", "gps_lng", "gps_accuracy_m", "anchor_x", "anchor_y", "anchor_t", "anchor_source"]) sitio[k] = sessionPayload[k];
        const { error: sitioErr } = await supabase.from("pest_scouting_sessions").update(sitio as any).eq("id", sessionId);
        if (sitioErr) throw sitioErr;
      }

      /* El parte ya existe: su análisis, atado a él (el adelantado si son las mismas
         fotos). No se espera: corre mientras se suben las fotos y se abre la ficha. */
      if (photos.length > 0) {
        analizarParte(sessionId, photos.map((p) => p.file), { lang: idiomaDelAnalisis(i18n.language), blockId });
      }
      const photoUrls = await uploadPhotos(sessionId);
      const audioPublicUrl = await uploadAudio(sessionId);

      const update: Record<string, unknown> = {};
      if (photoUrls.length > 0) {
        update.photo_urls = photoUrls;
        update.photo_url = photoUrls[0];
      }
      if (audioPublicUrl) update.audio_url = audioPublicUrl;
      /* El texto se guarda AQUÍ, recién transcrito. Antes vivía solo en la
         pantalla y se perdía al cerrarla, así que quien abría la ficha volvía a
         transcribir el mismo audio —con otro motor— y podían salir dos textos
         distintos del mismo parte. Ahora hay uno solo, y abrir la ficha no
         cuesta ni una llamada. */
      if (transcript.trim()) {
        update.voice_transcript = transcript.trim();
        update.voice_transcript_lang = transcriptLang;
      }
      if (lineNumber != null) {
        // Formato FIJO independiente del idioma: lo parsean el drawer, el
        // dashboard y el fallback del mapa (regex línea|line).
        update.notes = `Línea: ${lineNumber}`;
      }

      if (Object.keys(update).length > 0) {
        const { error: updErr } = await supabase
          .from("pest_scouting_sessions")
          .update(update as any)
          .eq("id", sessionId);
        if (updErr) console.error("session media update failed:", updErr);
      }

      // Confirmar ANTES de avisar: el aviso tiene que ver el parte como lo veía antes, ya clasificado.
      let unconfirmed = false;
      if (viaFoci) {
        const input = fociForm.buildInput(sessionId);
        const out = input ? await confirmSampling(input) : null;
        if (!out || !out.ok) {
          if (!out || out.error.kind === "legacy") {
            // Aquí no hay focos (empresa fuera del piloto o base sin migrar): se clasifica como siempre.
            const pest = classifyPests.find((p) => p.id === classifyPestId);
            const { error: legacyErr } = await supabase
              .from("pest_scouting_sessions")
              .update({
                triage_status: "classified",
                triage_pest_id: classifyPestId,
                triage_pest_name_snapshot: pest?.name ?? null,
                triaged_by: user.id,
                triaged_at: new Date().toISOString(),
              })
              .eq("id", sessionId);
            if (legacyErr) unconfirmed = true;
          } else {
            console.warn("confirmar con foco falló:", out.error.code);
            unconfirmed = true;
          }
        }
      }

      try {
        const { error: notifyErr } = await supabase.rpc(
          "notify_worker_quick_report" as never,
          { p_session_id: sessionId } as never,
        );
        if (notifyErr) console.warn("notify_worker_quick_report failed:", notifyErr);
      } catch (notifyEx) {
        console.warn("notify_worker_quick_report threw:", notifyEx);
      }

      toast({
        title: t("worker.incident.sentTitle"),
        description: unconfirmed ? t("pests.scouting.focusLink.savedUnconfirmed") : t("worker.incident.sentDesc"),
      });
      // Refresh every surface that counts pests so a new report (incl. the admin
      // audio-first "Muestreo") shows up instantly without a reload.
      invalidateScoutingData(queryClient);
      // Privileged entry points get a post-confirm shortcut (what next?).
      if ((allowClassification || followUpTaskProp) && onSubmitted) onSubmitted(sessionId, { followUpDone: !!followUpTask });
      onOpenChange(false);
    } catch (err) {
      console.error("Incident submit failed:", err);
      toast({ title: t("worker.incident.submitError"), variant: "destructive" });
      setSubmitting(false);
    }
  };

  /* ---------------- Navigation helpers ---------------- */

  const canNextFromCapture = (photos.length > 0 || !!audioBlob) && !recording;
  const goBack = () => setStep((s) => {
    if (followUpTask) return 0; // el sitio no se cambia: de la pregunta se vuelve a la captura
    let prev = s - 1;
    // The Línea step (4) auto-skips forward when the block has no lines, so
    // going back into it would bounce straight forward again — skip it here too.
    if (prev === 4 && lineOptions.length === 0) prev = 3;
    return Math.max(0, prev);
  });

  /* ---------------- Voice prefill ---------------- */

  /* Sin nota que analizar: la nave de la tarea que tiene en marcha, o si no la del GPS,
     con el sector y la línea que crean la tarea o el GPS YA PUESTOS (Pablo, 28-09-2026:
     «que no tengas que volver a clicarlo para confirmar»); se cambian con las pastillas
     de arriba, y si el GPS dice otra nave se avisa (`gpsMismatch`). Sin sector, a
     elegirlo. Sin nada de eso, el paso 1 de siempre. */
  const placeAfterCapture = () => {
    if (!locationId && !greenhouseId && !blockId) {
      // Lo que digan la tarea y el GPS AHORA, y nada de una sugerencia anterior.
      const merged = mergeVoiceAndGps(null, gpsGuessRef.current, { task: taskPlace });
      if (merged.greenhouseId) {
        const placed = applyWorkerPlace(merged);
        setStep(!placed.block ? 3 : placed.line ? 5 : 4);
        return;
      }
    }
    setStep(1);
  };

  /* Lo juntado de voz, tarea y GPS, aplicado: nave, sector y línea se PONEN (Pablo,
     28-09-2026: «que no tengas que volver a clicarlo para confirmar que sí»; antes sector y
     línea quedaban sugeridos para un toque, decisión G del 23-09). Lo dicho manda sobre el
     GPS (`mergeVoiceAndGps`). Se cambian con «Cambiar» o con las pastillas, y ahí sí se
     elige tocando. Las sugerencias se guardan igual: marcan en su pantalla de dónde salió.
     Devuelve qué quedó puesto, para saber a qué pantalla ir. */
  const applyWorkerPlace = (merged: MergedPlace): { block: boolean; line: boolean } => {
    setVoicePlacedBy(merged.from);
    setLocationId(merged.locationId);
    setGreenhouseId(merged.greenhouseId);
    const block = !!(merged.greenhouseId && merged.blockId && merged.from.block);
    // La línea DICHA se pone aunque falte el sector (vale para el que se elija); la del GPS, solo con su sector.
    const line = merged.lineNumber != null && (merged.from.line === "voice" || (block && merged.from.line === "gps"));
    setBlockId(block ? merged.blockId : null);
    setLineNumber(line ? merged.lineNumber : null);
    // Si ese sector no tiene esa línea (la dijo mal, o el GPS cae en el borde), se quita al llegar sus líneas.
    setLineCheckBlock(line && block ? merged.blockId : null);
    setBlockSuggested(block ? { greenhouseId: merged.greenhouseId!, blockId: merged.blockId!, from: merged.from.block! } : null);
    setLineSuggested(
      merged.lineNumber != null && (merged.from.line === "voice" || merged.from.line === "gps")
        ? // La que DIJO vale para el sector que elija; la del GPS, solo para su sector.
          { blockId: merged.from.line === "gps" ? merged.blockId : null, line: merged.lineNumber, from: merged.from.line }
        : null,
    );
    return { block, line };
  };

  /* EL GPS QUE LLEGA TARDE (28-09-2026). Si la nota se analiza antes de que el GPS sepa en qué
     nave está (cobertura, arranque en frío), lo que faltaba se quedaba vacío para siempre. Mientras
     se revisa «Esto es lo que entendí» / «¿Dónde estás?», en cuanto llega el GPS rellena lo que
     SIGUE vacío —nunca pisa lo dicho ni lo elegido—: la nave si no hay, y si es su nave, el sector
     y la línea. */
  useEffect(() => {
    const gps = gpsGuess.placement;
    if (!open || !gps || followUpTask || voicePhase !== "preview" || step !== 0) return;
    if (!greenhouseId) {
      const merged = mergeVoiceAndGps(voiceHeardRef.current, gps, { task: taskPlace });
      if (merged.greenhouseId) applyWorkerPlace(merged);
      return;
    }
    if (blockId || gps.greenhouseId !== greenhouseId || !gps.blockId) return;
    setBlockId(gps.blockId);
    setBlockSuggested({ greenhouseId, blockId: gps.blockId, from: "gps" });
    const lineaGps = lineNumber == null && gps.lineNumber != null;
    if (lineaGps) setLineNumber(gps.lineNumber);
    else if (lineNumber != null) setLineCheckBlock(gps.blockId);
    setVoicePlacedBy((p) => ({ ...(p ?? { greenhouse: null, block: null, line: null }), block: "gps", ...(lineaGps ? { line: "gps" as const } : {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gpsGuess.placement, voicePhase, step, greenhouseId, blockId]);

  /* EL TRABAJADOR, con la nota analizada (D28): lo dicho manda y el GPS rellena lo
     que falta (`mergeVoiceAndGps`). La plaga que entendió NO clasifica: viaja como
     sugerencia (`voice_guess`) para quien confirma. SIEMPRE se enseña «¿Dónde estás?»
     con lo entendido, lo puesto por el GPS y lo que NO se entendió (W1: antes, si no
     se entendía nada, pasaba en silencio a los pasos). En bambara y mandinka manda
     el GPS: la transcripción no es fiable para un sitio. */
  const placeWorkerFromVoice = (report: VoiceReport | null, miss: "empty" | "failed" | "skipped" | null = null, spokenLang: string | null = null) => {
    // Una finca fuera de SU alcance (el modelo ve todas) es como no haberla entendido:
    // antes fallaba al enviar con un error que nadie entendía (W8).
    const outOfScope = !!report?.location_id && !!allowedLocationIds && !allowedLocationIds.has(report.location_id);
    const heard: VoicePlace | null = report && !outOfScope
      ? {
          locationId: report.location_id ?? null,
          greenhouseId: report.greenhouse_id ?? null,
          blockId: report.block_ids?.[0] ?? null,
          lineNumber: report.observed_row ?? null,
          blockName: report.spoken_block_name ?? null,
        }
      : null;
    voiceHeardRef.current = heard;
    const gps = gpsGuessRef.current;
    // El idioma que detectó el motor, o el de la pantalla: en bm/man la voz no coloca NADA
    // si hay GPS (ni nave, ni sector, ni línea, ni finca); sin GPS, lo que haya.
    const langs = [spokenLang, i18n.language].filter(Boolean) as string[];
    const unreliable = !!gps && langs.some((l) => VOICE_PLACE_UNRELIABLE_LANGS.has(l));
    const merged = mergeVoiceAndGps(unreliable ? null : heard, gps, { task: taskPlace });
    // Siempre que hubo nota se guarda qué entendió (aunque sea nada): así se mide si la voz acierta (W9).
    setVoiceGuess(voiceGuessOf(report, merged.from, new Date(), spokenLang ?? i18n.language ?? null));
    setVoiceWarning(report?.location_warning ?? null);
    const nothing = !heard || (!heard.greenhouseId && !heard.blockId && heard.lineNumber == null && !heard.blockName);
    setVoiceMiss(miss ?? (nothing ? "empty" : null));
    applyWorkerPlace(merged);
    setSpokenBlock(unreliable ? null : heard?.blockName ?? null);
    setVoicePhase("preview");
  };

  /* «Estoy en {la nave o el sector del GPS}» (el botón del aviso): lo del GPS ENTERO, sin
     mezclar nada de la otra nave; sector y línea, sugeridos para un toque como siempre. */
  const goToGpsPlace = () => {
    const gps = gpsGuess.placement;
    if (!gps) return;
    const merged = mergeVoiceAndGps(null, gps, { prefer: "gps" });
    const placed = applyWorkerPlace(merged);
    // Lo que se mide de la voz tiene que decir de dónde salió al final.
    setVoiceGuess((g) => (g ? { ...g, placed: merged.from } : g));
    // Fuera de «¿Dónde estás?», a lo que falte (con sector y línea puestos, al resumen).
    if (step > 0) setStep(!placed.block ? 3 : placed.line ? 5 : 4);
  };

  /* El sector DICHO sin nave, en la nave donde está el parte (la de la tarea, la del GPS o
     la que elija): en cuanto llegan sus sectores, si hay UNO con ese nombre, se sugiere
     como «lo dijiste tú» —por encima de lo que crean la tarea o el GPS—. Si ya eligió
     sector, no se toca. */
  useEffect(() => {
    if (!spokenBlock || !greenhouseId || blocksFetching) return;
    if (blockSuggested?.from === "voice" && blockSuggested.greenhouseId === greenhouseId) return;
    // Lo dicho manda (28-09-2026): pisa el sector que puso el GPS o la tarea, nunca uno elegido a mano.
    const puestoSolo = !!blockId && blockSuggested?.blockId === blockId && blockSuggested.greenhouseId === greenhouseId;
    if (blockId && !puestoSolo) return;
    const id = matchSpokenBlock(spokenBlock, blocks);
    if (!id) return;
    if (id !== blockId) {
      setBlockId(id);
      // La línea del GPS era de SU sector; la que dijo vale para este.
      const dicha = lineSuggested?.from === "voice" ? lineSuggested.line : null;
      setLineNumber(dicha);
      setLineCheckBlock(dicha != null ? id : null);
    }
    setBlockSuggested({ greenhouseId, blockId: id, from: "voice" });
    setVoicePlacedBy((p) => (p ? { ...p, block: "voice" } : p));
    setVoiceGuess((g) => (g ? { ...g, placed: { ...g.placed, block: "voice" } } : g));
  }, [spokenBlock, greenhouseId, blockId, blocksFetching, blocks, blockSuggested, lineSuggested]);

  // Transcribe the recorded note, ask the AI to extract structure + pest +
  // severity, and pre-fill the existing form-state setters. Unresolved fields
  // stay null so the manual dropdowns still work. Falls back to the manual flow
  // on any failure.
  //
  // Para quien clasifica (allowClassification) rellena sitio, plaga y gravedad y
  // se envía desde el resumen. Para el TRABAJADOR (`workerVoice`, D28) rellena
  // solo el sitio, juntándolo con el GPS; con tope de espera, y si algo falla se
  // coloca por GPS y se sigue: el audio se guarda igual.
  const runVoicePrefill = async () => {
    if (!company?.id || !audioBlob) {
      setStep(1);
      return;
    }
    voiceTriedRef.current = true;
    voiceSkippedRef.current = false;
    const run = ++voiceRunRef.current;
    /** El asistente se cerró (o arrancó otro análisis): lo que llegue ya no es de este parte. */
    const stale = () => run !== voiceRunRef.current;
    setVoicePhase("interpreting");
    const abort = new AbortController();
    // «Saltar» (W6) aborta esto mismo: nadie espera mirando una rueda sin salida.
    voiceAbortRef.current = abort;
    const cap = workerVoice ? window.setTimeout(() => abort.abort(), VOICE_PREFILL_MAX_MS) : null;
    const conTope = <T,>(p: Promise<T>): Promise<T> =>
      Promise.race([
        p,
        new Promise<T>((_, reject) => {
          const fuera = () => reject(new Error("voice prefill timeout"));
          if (abort.signal.aborted) fuera();
          else abort.signal.addEventListener("abort", fuera, { once: true });
        }),
      ]);
    try {
      const ext = mimeToExt(audioMime);
      const formData = new FormData();
      formData.append("audio", audioBlob, `recording.${ext}`);
      formData.append("language", i18n.language);
      formData.append("company_id", company.id);
      /* Que lo guarde en el cubo de los partes, que es público y por tanto se
         puede REPRODUCIR luego desde la ficha. Así hay una sola copia: esta. */
      formData.append("bucket", "scouting-photos");
      formData.append("path", `${company.id}/voice/${crypto.randomUUID()}.${ext}`);

      const { data: session } = await supabase.auth.getSession();
      const transcribeRes = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-transcribe`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${session?.session?.access_token}` },
          body: formData,
          signal: abort.signal,
        },
      );
      if (!transcribeRes.ok) throw new Error("transcribe failed");
      const { text, audio_url, language } = await transcribeRes.json();
      if (stale()) return;
      const clean = (text || "").trim();
      setTranscript(clean);
      setTranscriptLang(language ?? null);
      if (audio_url) setStoredAudioUrl(audio_url);
      if (!clean) {
        // Nothing understood. Se VE (W1): «No te he entendido», y lo que ponga el GPS. También
        // quien clasifica (Pablo, 28-09-2026: «el GPS tiene que estar siempre»): antes caía a
        // elegir todo a mano aunque el GPS supiera la nave, el sector y la línea.
        if (workerVoice || allowClassification) {
          if (allowClassification) { setClassifyPestId(null); setPestUnknown(true); }
          placeWorkerFromVoice(null, "empty", language ?? null);
          return;
        }
        setVoicePhase("capture");
        setStep(1);
        return;
      }

      const { data, error } = await conTope(
        supabase.functions.invoke("voice-create-scouting", {
          body: { transcript: clean, company_id: company.id, language: i18n.language, audio_url: audio_url || null },
        }),
      );
      if (error) throw error;
      if (stale()) return;

      const report = (data?.report ?? null) as VoiceReport | null;
      if (workerVoice) {
        placeWorkerFromVoice(report, null, language ?? null);
        return;
      }
      if (report) {
        if (report.pest_id) {
          setClassifyPestId(report.pest_id);
          setPestUnknown(false);
        }
        if (report.severity) setSeverityLevel(report.severity as SeverityLevel);
      }
      /* Lo dicho vale para varias plagas (F7): nada elegido por él; se enseñan para un toque. */
      const opciones = (report?.pest_ambiguous ? report.pest_candidates ?? [] : []).filter((o) => classifyPests.some((p) => p.id === o.id));
      setVoicePestChoices(!report?.pest_id && opciones.length > 1 ? { spoken: report?.pest_spoken ?? "", options: opciones } : null);
      /* Si no dijo la plaga, «No lo sé» ya marcado (Pablo, 28-09-2026: «cuando no se dice,
         se pone "no lo sé" y ya está; lo puedes cambiar luego»). La plaga no hace falta para
         enviar: sale pendiente de clasificar y la IA de las fotos dice qué parece. */
      if (!report?.pest_id) {
        setClassifyPestId(null);
        setPestUnknown(true);
      }
      /* EL SITIO, igual que el del trabajador (Pablo, 28-09-2026: «si digo línea 17 y el GPS
         confirma que estoy por allí, el sector lo tendría que haber puesto directamente»):
         lo dicho manda y el GPS rellena lo que falte —nave, sector, línea—, ya puesto y con
         «Lo puso el GPS» en su fila. Antes aquí solo contaba la voz: sin decir sector no
         había sector, y sin entender nada se elegía todo a mano. */
      placeWorkerFromVoice(report, null, language ?? null);
    } catch (err) {
      if (stale()) return;
      console.error("voice prefill failed:", err);
      if (workerVoice || allowClassification) {
        // Sin análisis no se pierde nada: el audio viaja igual y el GPS coloca. Y se le DICE (W1):
        // si pulsó «Saltar», que lo saltó; si no, que no se pudo. Igual para quien clasifica (28-09).
        if (allowClassification) { setClassifyPestId(null); setPestUnknown(true); }
        placeWorkerFromVoice(null, voiceSkippedRef.current ? "skipped" : "failed", null);
        return;
      }
      // Quien pulsó «Saltar» ya sabe que no se analizó: sin aviso de error.
      if (!voiceSkippedRef.current) toast({ title: t("worker.incident.voicePrefillError"), variant: "destructive" });
      // Graceful fallback: manual selection with whatever we captured.
      setVoicePhase("capture");
      setStep(1);
    } finally {
      if (cap != null) window.clearTimeout(cap);
      if (voiceAbortRef.current === abort) voiceAbortRef.current = null;
    }
  };

  const skipVoiceAnalysis = () => {
    voiceSkippedRef.current = true;
    voiceAbortRef.current?.abort();
  };

  /**
   * Step-0 "Continuar". Lo que decide NO es por dónde entraste, es si hay un
   * audio: quien empezaba por fotos grababa una nota y nadie la escuchaba —
   * había dicho en voz alta dónde estaba la plaga y acababa eligiendo la
   * ubicación a mano (fallo 2026-08-11).
   *
   * Entrando por fotos NO hay «Siguiente»: el botón pasa a grabar en el sitio,
   * y por eso dice «Grabar audio». Es IGUAL para todos los perfiles (petición
   * del usuario 27-08-2026: «exactamente como lo tienen los responsables y los
   * administradores»). Antes esto colgaba de `allowClassification`, que solo
   * llevan las dos entradas de administración, así que el trabajador hacía sus
   * tres fotos y caía directo a las preguntas de ubicación sin que nadie le
   * ofreciera el audio.
   *
   * Lo que cambia según el perfil es lo que viene DESPUÉS de grabar, no esto:
   * con `allowClassification` el audio se transcribe y rellena ubicación,
   * plaga y gravedad; sin él, la nota se sube igual —`uploadAudio` no mira ese
   * permiso, así que el responsable la escucha— y se sigue por los pasos
   * manuales de siempre.
   *
   * Si el micro está denegado no se deja a nadie encerrado: `startRecording`
   * avisa y se cae a los pasos manuales.
   */
  /** ¿El botón de Continuar del paso 0 va a ponerse a grabar en vez de avanzar?
   *  Lo usa también su etiqueta, para que diga lo que de verdad hace. */
  const captureContinueStartsRecording =
    !audioBlob && photos.length > 0 && !recording;

  const handleCaptureContinue = async () => {
    if (captureContinueStartsRecording) {
      // Se cambia de pantalla solo si el micro ha arrancado de verdad; si está
      // denegado no se puede dejar al usuario en un callejón sin salida, así
      // que se sigue a mano (el aviso lo da ya `startRecording`).
      if (await startRecording()) setActiveCapture("voice");
      else setStep(followUpTask ? 5 : 1);
      return;
    }
    if (allowClassification && audioBlob) {
      runVoicePrefill();
      return;
    }
    // Desde una tarea de seguimiento no se elige sitio: ya viene puesto.
    if (followUpTask) {
      setStep(5);
      return;
    }
    // El trabajador (D28): la misma nota, analizada UNA vez, coloca el parte.
    if (workerVoice && audioBlob && !voiceTriedRef.current) {
      runVoicePrefill();
      return;
    }
    placeAfterCapture();
  };

  /* El trabajador sale de «Esto es lo que entendí» hacia lo que falte, y si no
     falta nada al resumen de siempre, con su pregunta «¿es lo mismo?». */
  const continueFromPreview = () => {
    if (!greenhouseId) {
      setStep(locationId ? 2 : 1);
      return;
    }
    // El sector y la línea se confirman con un toque en SU pantalla (lo sugerido va marcado, G).
    if (!blockId) {
      setStep(3);
      return;
    }
    if (lineOptions.length > 0 && (lineNumber == null || !lineOptions.includes(lineNumber))) {
      setStep(4);
      return;
    }
    setStep(5);
  };

  /* Las seis filas de «Esto es lo que entendí», ya editables ahí mismo: cada una
     abre un desplegable pequeño con sus opciones y corrige sin salir de la
     pantalla (antes había que ir a los pasos de uno en uno para cambiar algo que
     la voz había entendido mal). Se leen de la SELECCIÓN, no de lo que dijo el
     modelo, para que lo corregido se vea al momento; y elegir un nivel de arriba
     limpia los de abajo, la misma cascada que las pastillas de la cabecera. */
  const previewRows = useMemo<EditableRowSpec[]>(() => {
    const severityTone = severityLevel
      ? { low: PILL_COLORS.blue, medium: PILL_COLORS.amber, high: PILL_COLORS.red, critical: PILL_COLORS.red }[severityLevel]
      : undefined;
    const rows: EditableRowSpec[] = [
      {
        field: "location",
        icon: MapPin,
        label: t("worker.incident.locationField"),
        tone: "#16A971",
        value: locations.find((l) => l.id === locationId)?.name ?? null,
        options: locations.map((l) => ({ id: l.id, label: l.name })),
        selectedId: locationId,
        from: voicePlacedBy?.greenhouse ?? null,
        onSelect: (id) => {
          setLocationId(id);
          setGreenhouseId(null);
          setBlockId(null);
          setLineNumber(null);
          setVoicePlacedBy(null);
        },
      },
      {
        field: "greenhouse",
        icon: Warehouse,
        label: t("worker.incident.greenhouseLabel"),
        tone: "#16A971",
        value: greenhouses.find((g) => g.id === greenhouseId)?.name ?? null,
        options: greenhouses.map((g) => ({ id: g.id, label: g.name })),
        selectedId: greenhouseId,
        from: voicePlacedBy?.greenhouse ?? null,
        onSelect: (id) => {
          setGreenhouseId(id);
          setBlockId(null);
          setLineNumber(null);
          setVoicePlacedBy(null);
        },
      },
      {
        field: "block",
        icon: Grid3x3,
        label: t("worker.incident.blockLabel"),
        tone: "#16A971",
        value: blocks.find((b) => b.id === blockId)?.name ?? null,
        options: blocks.map((b) => ({ id: b.id, label: b.name })),
        layout: "grid" as const,
        cols: 2 as const,
        selectedId: blockId,
        from: voicePlacedBy?.block ?? null,
        onSelect: (id) => {
          setBlockId(id);
          setLineCheckBlock(id);
          setVoicePlacedBy((p) => (p ? { ...p, block: null } : p));
        },
      },
      {
        field: "line",
        icon: Rows3,
        label: t("worker.incident.lineLabel"),
        tone: "#16A971",
        value: lineNumber != null ? String(lineNumber) : null,
        options: lineOptions.map((n) => ({ id: String(n), label: `L${n}` })),
        layout: "grid" as const,
        cols: 4 as const,
        selectedId: lineNumber != null ? String(lineNumber) : null,
        from: voicePlacedBy?.line ?? null,
        onSelect: (id) => {
          setLineNumber(Number(id));
          setVoicePlacedBy((p) => (p ? { ...p, line: null } : p));
        },
      },
      {
        field: "pest",
        icon: Bug,
        label: t("worker.incident.pestField"),
        tone: "#E0962E",
        emphasis: true,
        value: classifyPestId
          ? classifyPests.find((p) => p.id === classifyPestId)?.name ?? null
          : pestUnknown
            ? t("worker.incident.pestUnknown")
            : null,
        // «No lo sé» la primera: saber la plaga no es obligatorio para avisar de ella.
        options: [{ id: PEST_UNKNOWN, label: t("worker.incident.pestUnknown") }, ...classifyPests.map((p) => ({ id: p.id, label: p.name }))],
        selectedId: pestUnknown ? PEST_UNKNOWN : classifyPestId,
        onSelect: (id) => {
          setClassifyPestId(id === PEST_UNKNOWN ? null : id);
          setPestUnknown(id === PEST_UNKNOWN);
        },
      },
      {
        field: "severity",
        icon: Gauge,
        label: t("worker.incident.severityField"),
        tone: severityTone,
        value: severityLevel ? t(`worker.incident.severityLevels.${severityLevel}`) : null,
        options: SEVERITY_OPTIONS.map((s) => ({ id: s, label: t(`worker.incident.severityLevels.${s}`) })),
        layout: "grid" as const,
        cols: 2 as const,
        selectedId: severityLevel,
        onSelect: (id) => setSeverityLevel(id as SeverityLevel),
      },
    ];
    // El trabajador no clasifica: plaga y gravedad no son suyas (lo que entendió
    // la voz viaja aparte como sugerencia, D28). Solo el sitio.
    return allowClassification ? rows : rows.filter((r) => r.field !== "pest" && r.field !== "severity");
  }, [
    locations, greenhouses, blocks, lineOptions, classifyPests, allowClassification,
    locationId, greenhouseId, blockId, lineNumber, classifyPestId, pestUnknown, severityLevel, t, voicePlacedBy,
  ]);

  /* Se envía DESDE el resumen: lo que falte se rellena con su lápiz, ahí mismo.
     Las condiciones son las mismas que guarda `handleSubmit` — que el botón
     esté apagado es más honesto que dejarlo pulsar para soltar un aviso. */
  const canSubmitFromPreview =
    !!greenhouseId && !!blockId && (lineOptions.length === 0 || lineNumber != null);

  // "Volver a grabar" — discard the note + any prefill and record again.
  const backToCaptureFromPreview = () => {
    removeAudio();
    setLocationId(null);
    setGreenhouseId(null);
    setBlockId(null);
    setLineNumber(null);
    setClassifyPestId(null);
    setPestUnknown(false);
    setSeverityLevel(null);
    setVoiceWarning(null);
    setTranscript("");
    setVoicePhase("capture");
  };

  const isDirty =
    photos.length > 0 ||
    !!audioBlob ||
    !!locationId ||
    !!greenhouseId ||
    !!blockId ||
    lineNumber != null;

  /* ---------------- Render ---------------- */

  const breadcrumb = [
    greenhouses.find((g) => g.id === greenhouseId)?.name,
    activeBlock?.name,
    lineNumber != null ? `L${lineNumber}` : null,
  ]
    .filter(Boolean)
    .join(" › ");

  const locationName = locations.find((l) => l.id === locationId)?.name;
  const greenhouseName = greenhouses.find((g) => g.id === greenhouseId)?.name;

  /* El aviso de «el GPS dice otro sitio», con los nombres de los dos sitios. La finca del
     GPS se nombra solo si es OTRA: «Nave 5 (Campohermoso)» cuando estás en Las Norias. */
  const gpsMismatchBanner =
    gpsMismatch && gpsMismatchNames?.greenhouse ? (
      <GpsMismatchBanner
        level={gpsMismatch.level}
        gpsName={
          gpsMismatch.level === "block"
            ? gpsMismatchNames.block ?? "?"
            : gpsMismatchNames.locationId && gpsMismatchNames.locationId !== locationId && gpsMismatchNames.location
              ? `${gpsMismatchNames.greenhouse} (${gpsMismatchNames.location})`
              : gpsMismatchNames.greenhouse
        }
        hereName={
          gpsMismatch.level === "block"
            ? blocks.find((b) => b.id === (blockId ?? blockSuggested?.blockId))?.name ?? "?"
            : greenhouseName ?? "?"
        }
        onUseGps={goToGpsPlace}
        t={t}
      />
    ) : null;

  const headerSelections = (
    <div className="space-y-2 mb-4">
      {locationId && (
        <SelectionPill
          label={t("worker.incident.fincaLabel")}
          value={locationName ?? "—"}
          onChange={() => {
            setGreenhouseId(null);
            setBlockId(null);
            setLineNumber(null);
            setStep(1);
          }}
          changeLabel={t("worker.incident.change")}
        />
      )}
      {greenhouseId && (
        <SelectionPill
          label={t("worker.incident.greenhouseLabel")}
          value={greenhouseName ?? "—"}
          onChange={() => {
            setGreenhouseId(null);
            setBlockId(null);
            setLineNumber(null);
            setStep(2);
          }}
          changeLabel={t("worker.incident.change")}
        />
      )}
      {blockId && (
        <SelectionPill
          label={t("worker.incident.blockLabel")}
          value={activeBlock?.name ?? "—"}
          onChange={() => {
            setBlockId(null);
            setLineNumber(null);
            setStep(3);
          }}
          changeLabel={t("worker.incident.change")}
        />
      )}
      {lineNumber != null && (
        <SelectionPill
          label={t("worker.incident.lineLabel")}
          value={`L${lineNumber}`}
          onChange={() => {
            setLineNumber(null);
            setStep(4);
          }}
          changeLabel={t("worker.incident.change")}
        />
      )}
    </div>
  );

  const footer = (() => {
    if (step === 0) {
      // Interpreting the voice note — lock the footer with a spinner.
      if (voicePhase === "interpreting") {
        return (
          <>
            {/* A los 5 s, «Saltar»: se sigue sin el análisis y la nota se guarda igual (W6). */}
            {canSkipVoice ? (
              <FormSheetSecondaryButton onClick={skipVoiceAnalysis} data-testid="voice-skip">
                {t("worker.incident.skip")}
              </FormSheetSecondaryButton>
            ) : (
              <span className="flex-1" />
            )}
            <FormSheetPrimaryButton onClick={() => {}} disabled>
              <Loader2 className="w-5 h-5 animate-spin" /> {t("worker.incident.continue")}
            </FormSheetPrimaryButton>
          </>
        );
      }
      // El resumen ES la pantalla de confirmación: se corrige ahí mismo con los
      // lápices y se envía desde ahí. Antes, «Continuar» llevaba a un paso que
      // repetía la ubicación con botones de «Cambiar» — lo mismo, otra vez.
      if (voicePhase === "preview") {
        // El trabajador no envía desde aquí: sigue al parte de siempre (su pregunta
        // «¿es lo mismo?» vive en el resumen, D28). Quien clasifica sí envía.
        if (!allowClassification) {
          return (
            <>
              <FormSheetSecondaryButton onClick={backToCaptureFromPreview}>
                ← {t("worker.incident.recordAgain")}
              </FormSheetSecondaryButton>
              <FormSheetPrimaryButton onClick={continueFromPreview} data-testid="voice-continue">
                {t("worker.incident.continue")} <ChevronRight className="w-5 h-5" />
              </FormSheetPrimaryButton>
            </>
          );
        }
        return (
          <>
            <FormSheetSecondaryButton onClick={backToCaptureFromPreview}>
              ← {t("worker.incident.recordAgain")}
            </FormSheetSecondaryButton>
            <FormSheetPrimaryButton onClick={handleSubmit} disabled={!canSubmitFromPreview || submitting || fociBlocked} data-testid="wizard-submit">
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Check className="w-5 h-5" />
              )}
              {t("worker.incident.confirmSampling")}
            </FormSheetPrimaryButton>
          </>
        );
      }
      return (
        <>
          <FormSheetSecondaryButton
            onClick={() => {
              // Si se ha llegado a grabar VINIENDO de las fotos, atrás es volver a
              // las fotos: cerrar el asistente entero por retroceder un paso sería
              // tirar lo que ya se ha hecho. Lo ya grabado se conserva —al seguir,
              // se analiza—; solo se descarta lo que estuviera a medias. El
              // aplazamiento es por la carrera con `onstop`, que crea el blob
              // DESPUÉS de parar (mismo truco que el botón «Volver a grabar»).
              if (activeCapture === "voice" && photos.length > 0) {
                if (recording) {
                  stopRecording();
                  setTimeout(() => removeAudio(), 50);
                }
                setActiveCapture("photo");
                return;
              }
              // Vuelve al selector de foto/voz siempre que quien nos abrió sepa cómo
              // volver a enseñarlo, venga del conmutador de voz o de la página de
              // Plagas. Antes solo lo hacía el conmutador y el resto cerraba de golpe,
              // así que cambiar de foto a voz obligaba a empezar de cero.
              if (onRequestTabSelector) {
                onRequestTabSelector();
              } else {
                onOpenChange(false);
              }
            }}
          >
            ← {t("common.back")}
          </FormSheetSecondaryButton>
          <FormSheetPrimaryButton
            onClick={handleCaptureContinue}
            disabled={!canNextFromCapture}
          >
            {/* Con fotos y sin audio, este botón NO continúa: se pone a grabar.
                Que lo diga, y con el icono del micro. */}
            {captureContinueStartsRecording ? (
              <>
                {t("worker.incident.recordAudio")} <Mic className="w-5 h-5" />
              </>
            ) : (
              <>
                {t("worker.incident.continue")} <ChevronRight className="w-5 h-5" />
              </>
            )}
          </FormSheetPrimaryButton>
        </>
      );
    }
    if (step >= 1 && step <= 4) {
      return (
        <>
          <FormSheetSecondaryButton onClick={goBack}>
            ← {t("common.back")}
          </FormSheetSecondaryButton>
          <span className="flex-1" />
        </>
      );
    }
    // step === 5: resumen
    return (
      <>
        <FormSheetSecondaryButton onClick={goBack}>
          ← {t("common.back")}
        </FormSheetSecondaryButton>
        <FormSheetPrimaryButton onClick={handleSubmit} disabled={submitting || gpsBlocking || fociBlocked || followAsking} data-testid="wizard-submit">
          {submitting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Check className="w-5 h-5" />
          )}
          {t("worker.incident.confirmSampling")}
        </FormSheetPrimaryButton>
      </>
    );
  })();


  /* La hoja se estira a pantalla completa en cuanto hay algo que MIRAR: una foto
     hecha o el micro en marcha. Igual que la del asistente y la de Tarea por voz,
     y por el mismo motivo — a partir de ahí esto deja de ser una pregunta corta y
     pasa a ser algo que se revisa (la foto, el dictado, la ubicación, la plaga).
     Antes de eso sigue corta: la pantalla de captura es un botón y poco más. */
  const expanded = photos.length > 0 || recording || !!audioBlob || voicePhase !== "capture";

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      isDirty={isDirty}
      /* El alto lo decide el CONTENIDO de cada paso (petición del usuario
         27-08-2026): «elegir ubicación» son tres botones y no tiene por qué
         ocupar la pantalla; «elegir la línea» son veinte y sí. `auto` mide y solo
         sube el techo cuando se desborda —no hay suelo, a diferencia de `page`,
         que dejaría los tres botones abriendo igual de altos.

         El paso 0 se queda EXACTAMENTE como estaba —`expanded` con foto o audio,
         `page` sin ellos—: `CaptureStep` centra el micrófono con `min-h-full`
         contra un cuerpo que tiene alto de verdad, y con alto automático ese
         centrado se viene abajo. Además es justo el paso en el que la hoja no
         debe moverse, que es mientras se está grabando.

         `scrollResetKey` de abajo es lo que lo hace posible paso a paso: al
         cambiar de paso suelta el enganche de `useSheetFit` y vuelve a medir, así
         que la hoja también puede ENCOGER al retroceder. */
      fit={step === 0 ? (expanded ? "expanded" : "page") : "auto"}
      size="tall"
      flush
      /* Anclado: misma esquina, mismo alto y mismo ancho que el conmutador de
         voz del que viene. Sin esto, elegir Foto o Voz saltaba de la esquina a
         una ventana centrada a mitad del flujo.

         Las tres cosas van desde `md`, no desde `lg`: el conmutador se ancla a
         la esquina ya en tableta y es más bajo allí, así que el alto de tableta
         y el ancho tienen que acompañarlo o el salto vuelve, solo que una
         pantalla más tarde. `VOICE_PANEL_H_TABLET_CLASS` va DESPUÉS del alto
         base a propósito: mismo grupo de tailwind-merge, gana la última.

         Solo en la rama `corner`. El parte del trabajador (`anchor="center"`)
         no ha pedido nada y se queda exactamente como estaba. */
      contentClassName={anchor === "corner"
        ? cn(
            SHEET_CORNER_OVERRIDE_CLASS,
            VOICE_PANEL_H_CLASS,
            VOICE_PANEL_H_TABLET_CLASS,
            "md:max-w-[450px]",
          )
        : undefined}
      scrollResetKey={`${step}-${voicePhase}`}
      title={
        step === 0
          ? t("worker.incident.reportTitle")
          : t("worker.incident.reportTitle")
      }
      subtitle={
        step >= 1 && step <= 4
          ? t("worker.incident.whereTaken")
          : step === 0
            ? (voicePhase === "preview"
                ? t(allowClassification ? "worker.incident.voicePreviewTitle" : "worker.incident.place.title")
                : t("worker.incident.captureSubtitle"))
            : t("worker.incident.summarySubtitle")
      }
      footer={footer}
    >
      {/* Desde una tarea de seguimiento el sitio no se cambia: sin pastillas de «Cambiar».
          Y mientras se pregunta tampoco salen: en un móvil empujaban las respuestas
          fuera de la pantalla, y ahí solo hay UNA cosa que decidir. */}
      {step >= 2 && !followUpTask && !(step === 5 && followAsking) && headerSelections}

      {step === 0 && voicePhase === "capture" && (
        <CaptureStep
          activeCapture={activeCapture}
          setActiveCapture={setActiveCapture}
          photos={photos}
          audioUrl={audioUrl}
          recording={recording}
          recordSeconds={recordSeconds}
          onOpenCamera={openCamera}
          onOpenGallery={openGallery}
          onRemovePhoto={removePhoto}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          onRemoveAudio={removeAudio}
          t={t}
        />
      )}

      {step === 0 && voicePhase === "interpreting" && (
        <ProcessingState transcript={transcript} />
      )}

      {step === 0 && voicePhase === "preview" && (
        <div className="space-y-4">
          {allowClassification && voiceMiss && (
            <p className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-800" data-testid="worker-place-miss" data-miss={voiceMiss}>
              <CircleHelp className="mt-0.5 h-5 w-5 shrink-0" />
              {t(voicePlacedBy?.greenhouse === "gps" ? "worker.incident.place.notUnderstoodGps" : voiceMiss === "failed" ? "worker.incident.place.failed" : voiceMiss === "skipped" ? "worker.incident.place.skipped" : "worker.incident.place.notUnderstood")}
            </p>
          )}
          {allowClassification && gpsMismatchBanner}
          {allowClassification ? (
            <>
              <VoicePreview
                transcript={transcript}
                rows={previewRows}
                warning={voiceWarning}
                // Sin plaga (sin elegir o «No lo sé») se dice qué pasa: no es un error, se clasifica luego.
                note={!classifyPestId && !voicePestChoices ? t("worker.incident.pestUnknownHint") : null}
                t={t}
              />
              {voicePestChoices && (
                <VoicePestChoice
                  spoken={voicePestChoices.spoken}
                  options={voicePestChoices.options}
                  selectedId={classifyPestId}
                  onPick={(id) => {
                    setClassifyPestId(id);
                    setPestUnknown(false);
                  }}
                />
              )}
            </>
          ) : (
            /* EL TRABAJADOR (W1/H, 23-09-2026): «¿Dónde estás?» con un semáforo por fila
               —lo dijo él, es su tarea, lo puso el GPS, no se entendió— y, si el GPS dice
               otro sitio, el aviso con su botón (24-09). Nada pasa en silencio. */
            <WorkerPlacePreview
              transcript={transcript}
              miss={voiceMiss}
              missByGps={voicePlacedBy?.greenhouse === "gps"}
              mismatch={gpsMismatchBanner}
              rows={[
                { field: "greenhouse", icon: Warehouse, label: t("worker.incident.greenhouseLabel"), value: greenhouseName ?? null, from: greenhouseId ? voicePlacedBy?.greenhouse ?? "voice" : null },
                { field: "block", icon: Grid3x3, label: t("worker.incident.blockLabel"), value: blockId ? blocks.find((b) => b.id === blockId)?.name ?? null : null, from: suggestedBlockHere?.blockId === blockId ? suggestedBlockHere?.from ?? null : null },
                { field: "line", icon: Rows3, label: t("worker.incident.lineLabel"), value: lineNumber != null ? String(lineNumber) : null, from: lineSuggested?.line === lineNumber ? lineSuggested?.from ?? null : null },
              ]}
              onChange={(field) => setStep(field === "greenhouse" ? (locationId ? 2 : 1) : field === "block" || !blockId ? 3 : 4)}
              warning={voiceWarning}
              t={t}
            />
          )}
          {fociActive && (
            <div className="rounded-2xl border border-[#b6dded] bg-white p-4">
              <ConfirmSamplingFields form={fociForm} showSeverity={false} />
            </div>
          )}
          <GpsBanner geo={geo} blocking={gpsBlocking} far={farFromGreenhouse && !gpsMismatch} t={t} />
          <PreviewResumen
            audioSeconds={audioBlob ? recordSeconds : 0}
            photoCount={photos.length}
            breadcrumb={breadcrumb}
            pestName={classifyPests.find((p) => p.id === classifyPestId)?.name ?? null}
            severityLabel={severityLevel ? t(`worker.incident.severityLevels.${severityLevel}`) : null}
            t={t}
          />
        </div>
      )}

      {step === 1 && (
        <FincaStep
          locations={locations}
          onPick={(id) => {
            setLocationId(id);
            setGreenhouseId(null);
            setBlockId(null);
            setLineNumber(null);
            setStep(2);
          }}
          t={t}
        />
      )}

      {step === 2 && (
        <GreenhouseStep
          greenhouses={greenhouses}
          greenhouseId={greenhouseId}
          onPick={(id) => {
            setGreenhouseId(id);
            setBlockId(null);
            setLineNumber(null);
            setStep(3);
          }}
          t={t}
        />
      )}

      {(step === 3 || step === 4) && gpsMismatchBanner}

      {step === 3 && (
        <BlockStep
          blocks={blocks}
          blockId={blockId}
          suggested={suggestedBlockHere}
          onPick={(id) => {
            setBlockId(id);
            setLineCheckBlock(id);
            // La línea que DIJO vale para el sector que elija (28-09-2026): puesta, sin otro toque.
            const dicha = lineSuggested && (lineSuggested.blockId == null || lineSuggested.blockId === id) ? lineSuggested.line : null;
            if (dicha != null) {
              setLineNumber(dicha);
              setStep(5);
            } else {
              setStep(4);
            }
          }}
          t={t}
        />
      )}

      {step === 4 && (
        <LineStep
          lineOptions={lineOptions}
          lineNumber={lineNumber}
          suggested={suggestedLineHere ? suggestedLineHere.line : null}
          suggestedFrom={suggestedLineHere?.from ?? null}
          accuracyM={geo.position?.accuracyM ?? null}
          onPick={(n) => {
            setLineNumber(n);
            setStep(5);
          }}
          t={t}
        />
      )}

      {step === 5 && followStage === "waiting" && (
        <div className="flex flex-col items-center gap-3 py-8" data-testid="followup-waiting">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          {/* Una rueda sin texto parecía colgada (W6): se dice qué hace, y se puede saltar. */}
          <p className="text-sm text-muted-foreground">{t("worker.incident.followup.checking")}</p>
          {canSkipFollow && (
            <FormSheetSecondaryButton
              onClick={() => { setFollowSkipped(true); setFollowSkipReason("timeout"); }}
              data-testid="followup-skip"
            >
              {t("worker.incident.skip")}
            </FormSheetSecondaryButton>
          )}
        </div>
      )}

      {/* En la pregunta se esconden las pastillas: que al menos sepa de QUÉ línea se le habla. */}
      {step === 5 && (followStage === "ask_same" || followStage === "ask_how") && breadcrumb && (
        <p className="text-center text-sm font-semibold text-slate-700" data-testid="followup-where">
          {t("worker.incident.followup.where", { place: breadcrumb })}
        </p>
      )}

      {step === 5 && (followStage === "ask_same" || followStage === "ask_how") && followCandidate && (
        <FollowUpStep
          candidate={followCandidate}
          candidates={followUpTask ? [] : followUp.candidates}
          stage={followStage}
          fromTask={!!followUpTask}
          onAnswer={(a, chosen) => { setFollowChosen(chosen ?? null); setFollowAnswer(a); }}
          onVerdict={setFollowVerdict}
          onNotHere={() => {
            setFollowDetached(true);
            setLocationId(null);
            setGreenhouseId(null);
            setBlockId(null);
            setLineNumber(null);
            setStep(1);
          }}
        />
      )}

      {step === 5 && !followAsking && (
        <>
        {gpsMismatchBanner}
        <GpsBanner geo={geo} blocking={gpsBlocking} far={farFromGreenhouse && !gpsMismatch} t={t} />
        <SummaryStep
          photoCount={photos.length}
          audioSeconds={audioBlob ? recordSeconds : 0}
          breadcrumb={breadcrumb}
          t={t}
          canClassify={allowClassification}
          pests={classifyPests}
          selectedPestId={classifyPestId}
          onPestSelect={setClassifyPestId}
          severity={severityLevel}
          onSeverityChange={setSeverityLevel}
          focusSlot={fociActive ? <ConfirmSamplingFields form={fociForm} showSeverity={false} className="pt-2" /> : null}
        />
        </>
      )}

      {/* Hidden inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPhotoInput}
      />
      {/* Galería: sin `capture` (por eso es otro input) y `multiple`, para poder
          elegir las tres de una vez. `.heic/.heif` a mano porque un iPhone no
          siempre rellena el tipo MIME y con `image/*` a secas te deja las fotos
          en gris, sin poder tocarlas. */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        className="hidden"
        onChange={onPhotoInput}
      />
    </FormSheet>
  );
}

/* ============================================================
 * Step components
 * ============================================================ */

function SelectionPill({
  label,
  value,
  onChange,
  changeLabel,
}: {
  label: string;
  value: string;
  onChange: () => void;
  changeLabel: string;
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-2xl bg-white border border-[#b6dded] shadow-sm">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#16A971", boxShadow: "0 6px 14px #16A97142, inset 0 1px 0 rgba(255,255,255,0.28)" }}>
        <MapPin className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">
          {label}
        </p>
        <p className="text-sm font-semibold text-foreground truncate">{value}</p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="rounded-full text-foreground font-semibold h-8 px-3"
        onClick={onChange}
      >
        {changeLabel}
      </Button>
    </div>
  );
}

interface TFn {
  (key: string, opts?: Record<string, unknown>): string;
}

/**
 * «Elegir de la galería», el segundo camino de la foto.
 *
 * Las casillas y el botón gordo abren la CÁMARA, que es lo que se quiere: la
 * plaga se fotografía delante de la planta. Pero una foto de hace dos horas —la
 * que hizo el encargado esta mañana, la del móvil que no llevaba encima— no
 * tenía por dónde entrar en el parte.
 *
 * Va como renglón discreto debajo de las casillas y no como una tercera tarjeta:
 * es la excepción, y darle el mismo peso cambiaría cuál de las dos cosas parece
 * la normal.
 */
function GalleryPickRow({ onOpenGallery, t }: { onOpenGallery: () => void; t: TFn }) {
  return (
    <button
      type="button"
      onClick={onOpenGallery}
      className="mt-3 w-full flex items-center justify-center gap-2 rounded-2xl border border-[#b6dded] bg-white px-4 py-2.5 transition-transform active:scale-[0.98]"
    >
      <Images className="w-[18px] h-[18px] shrink-0 text-[#2E84DE]" strokeWidth={1.9} />
      {/* `min-w-0` + `truncate`: en árabe o en francés esto es largo y no puede
          ensanchar la hoja. */}
      <span className="min-w-0 truncate text-sm font-semibold text-[#13314A]">
        {t("common.capture.chooseFromGallery")}
      </span>
      <span className="shrink-0 text-xs text-slate-500">
        · {t("worker.incident.maxPhotos", { max: MAX_PHOTOS })}
      </span>
    </button>
  );
}

function CaptureStep({
  activeCapture,
  photos,
  audioUrl,
  recording,
  recordSeconds,
  onOpenCamera,
  onOpenGallery,
  onRemovePhoto,
  onStartRecording,
  onStopRecording,
  onRemoveAudio,
  t,
}: {
  activeCapture: InitialMode;
  setActiveCapture: (m: InitialMode) => void;
  photos: CapturedPhoto[];
  audioUrl: string | null;
  recording: boolean;
  recordSeconds: number;
  onOpenCamera: () => void;
  onOpenGallery: () => void;
  onRemovePhoto: (id: string) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onRemoveAudio: () => void;
  t: TFn;
}) {
  const fmt = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (activeCapture === "voice") {
    return (
      /* Centrado vertical: mientras solo está el micro, la pantalla se queda
         medio vacía por abajo y el botón pegado arriba. `min-h-full` mide contra
         el cuerpo de la hoja (que sí tiene alto: `flex-1 min-h-0` en FormSheet),
         así que con poco contenido se centra y, en cuanto aparecen la
         reproducción y las fotos, crece y hace scroll como siempre. */
      <div className="flex min-h-full flex-col justify-center gap-6">
        {/* Mic block */}
        <div className="flex flex-col items-center text-center pt-2">
          <p className="text-base font-bold text-[#0c1f3d] mb-5">
            {audioUrl
              ? t("worker.incident.audioRecorded", { defaultValue: "Nota grabada" })
              : t("worker.incident.recordVoiceNote", {
                  defaultValue: "Graba tu nota de voz",
                })}
          </p>
          <button
            onClick={
              audioUrl
                ? undefined
                : recording
                  ? onStopRecording
                  : onStartRecording
            }
            disabled={!!audioUrl}
            data-testid="voice-record-toggle"
            data-recording={recording ? "true" : "false"}
            className={cn(
              "relative w-28 h-28 rounded-full flex items-center justify-center text-white shadow-[0_15px_40px_rgba(94,180,201,0.45)] active:scale-[0.97] transition-transform",
              recording && "ring-4 ring-rose-300/60 animate-pulse",
            )}
            style={{
              background: recording
                ? "linear-gradient(135deg, #ff7676 0%, #e85d3a 100%)"
                : "linear-gradient(135deg, #5eb4c9 0%, #4a9eb8 100%)",
            }}
          >
            {recording ? (
              <Square className="w-12 h-12" strokeWidth={2} />
            ) : (
              <Mic className="w-12 h-12" strokeWidth={2} />
            )}
          </button>
          <p className="mt-5 text-4xl font-bold tracking-tight text-[#0c1f3d] tabular-nums">
            {fmt(recordSeconds)}
          </p>
        </div>

        {/* Audio playback row when recorded */}
        {audioUrl && (
          <>
            <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <audio src={audioUrl} controls className="flex-1 max-w-full" />
              <button
                onClick={onRemoveAudio}
                aria-label={t("common.remove")}
                className="text-rose-500 p-1"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
            <button
              onClick={() => {
                onRemoveAudio();
                setTimeout(() => onStartRecording(), 50);
              }}
              className="block mx-auto text-sm font-semibold text-slate-500"
            >
              {t("worker.incident.recordAgain", {
                defaultValue: "Volver a grabar",
              })}
            </button>

            {/* Add photos block */}
            <div className="pt-2">
              <div className="flex items-baseline gap-2 mb-1">
                <h3 className="font-bold text-[#0c1f3d]">
                  {t("worker.incident.addPhotos", {
                    defaultValue: "Añadir fotos",
                  })}
                </h3>
                <span className="text-sm text-slate-500">
                  · {t("worker.incident.optional", { defaultValue: "opcional" })}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                {photos.length}/{MAX_PHOTOS}{" "}
                {t("worker.incident.added", { defaultValue: "añadidas" })}
              </p>
              <div className="grid grid-cols-3 gap-3">
                {Array.from({ length: MAX_PHOTOS }).map((_, i) => {
                  const p = photos[i];
                  if (p) {
                    return (
                      <div
                        key={p.id}
                        className="relative aspect-square rounded-2xl overflow-hidden border border-slate-200"
                      >
                        <img src={p.preview} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => onRemovePhoto(p.id)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                          aria-label={t("common.remove")}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  }
                  const isFirst = i === photos.length;
                  return (
                    <button
                      key={`slot-${i}`}
                      onClick={onOpenCamera}
                      className={cn(
                        "aspect-square rounded-2xl border-2 border-dashed flex items-center justify-center transition-colors",
                        isFirst
                          ? "border-teal-300 bg-teal-50/60 text-teal-500"
                          : "border-slate-200 bg-slate-50 text-slate-400",
                      )}
                    >
                      {isFirst ? (
                        <Camera className="w-7 h-7" strokeWidth={1.8} />
                      ) : (
                        <Plus className="w-7 h-7" strokeWidth={1.8} />
                      )}
                    </button>
                  );
                })}
              </div>
              <GalleryPickRow onOpenGallery={onOpenGallery} t={t} />
            </div>
          </>
        )}
      </div>
    );
  }

  // Photo mode
  return (
    /* Centrado en vertical DENTRO de la hoja, y con las piezas a un tamaño acorde:
       desde que la hoja se estira a pantalla completa con la primera foto, este
       bloque se quedaba arriba, pequeño, con media pantalla vacía debajo.
       El centrado va en un ENVOLTORIO propio con `my-auto`, y no repartiendo
       `mt-auto` + `mb-auto` entre los dos hijos: `space-y-*` no solo escribe el
       `margin-top` que separa hermanos, también un `margin-bottom: 0`, y ese le
       ganaba al `mb-auto` — todo el hueco se iba arriba y el bloque acababa
       pegado al pie, que es justo lo que se veía.
       Y no con `justify-center`: en una caja con scroll, centrar recorta por
       ARRIBA en cuanto el contenido crece (ya está escrito en
       VOICE_PANEL_BODY_CLASS). Con `my-auto`, si no sobra sitio los márgenes se
       quedan en cero y el bloque vuelve a fluir normal. */
    <div className="min-h-full flex flex-col">
      <div className="space-y-6 my-auto">
        <div className="flex flex-col items-center text-center">
          <p className="text-lg font-bold text-[#0c1f3d] mb-5">
            {t("worker.incident.takePhotosTitle", {
              defaultValue: "Haz fotos de la plaga",
            })}
          </p>
          <button
            onClick={onOpenCamera}
            className="w-36 h-36 rounded-full flex items-center justify-center text-white shadow-[0_15px_40px_rgba(232,93,58,0.35)] active:scale-[0.97] transition-transform"
            style={{
              background:
                "linear-gradient(135deg, #ff9a6b 0%, #ff7b51 55%, #e85d3a 100%)",
            }}
          >
            <Camera className="w-14 h-14" strokeWidth={2} />
          </button>
          <p className="mt-5 text-base text-slate-500">
            {photos.length}/{MAX_PHOTOS}{" "}
            {t("worker.incident.added", { defaultValue: "añadidas" })}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: MAX_PHOTOS }).map((_, i) => {
            const p = photos[i];
            if (p) {
              return (
                <div
                  key={p.id}
                  className="relative aspect-square rounded-2xl overflow-hidden border border-slate-200"
                >
                  <img src={p.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => onRemovePhoto(p.id)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                    aria-label={t("common.remove")}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            }
            const isFirst = i === photos.length;
            return (
              <button
                key={`slot-${i}`}
                onClick={onOpenCamera}
                className={cn(
                  "aspect-square rounded-2xl border-2 border-dashed flex items-center justify-center transition-colors",
                  isFirst
                    ? "border-orange-300 bg-orange-50/60 text-orange-500"
                    : "border-slate-200 bg-slate-50 text-slate-400",
                )}
              >
                {isFirst ? (
                  <Camera className="w-7 h-7" strokeWidth={1.8} />
                ) : (
                  <Plus className="w-7 h-7" strokeWidth={1.8} />
                )}
              </button>
            );
          })}
        </div>
        <GalleryPickRow onOpenGallery={onOpenGallery} t={t} />
      </div>
    </div>
  );
}


function FincaStep({
  locations,
  onPick,
  t,
}: {
  locations: { id: string; name: string }[];
  onPick: (id: string) => void;
  t: TFn;
}) {
  if (locations.length === 0) {
    return (
      <p className="text-sm text-center text-muted-foreground py-8">
        {t("worker.incident.noLocations")}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        {t("worker.incident.pickFinca")}
      </h2>
      <div className="grid grid-cols-1 gap-2">
        {locations.map((l) => (
          <BigOption key={l.id} label={l.name} onClick={() => onPick(l.id)} />
        ))}
      </div>
    </div>
  );
}

function GreenhouseStep({
  greenhouses,
  greenhouseId,
  onPick,
  t,
}: {
  greenhouses: { id: string; name: string }[];
  greenhouseId: string | null;
  onPick: (id: string) => void;
  t: TFn;
}) {
  if (greenhouses.length === 0) {
    return (
      <p className="text-sm text-center text-muted-foreground py-8">
        {t("worker.incident.noGreenhouses")}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        {t("worker.incident.pickGreenhouse")}
      </h2>
      <div className="grid grid-cols-2 gap-2">
        {greenhouses.map((g) => (
          <BigOption
            key={g.id}
            label={g.name}
            active={g.id === greenhouseId}
            onClick={() => onPick(g.id)}
          />
        ))}
      </div>
    </div>
  );
}

function BlockStep({
  blocks,
  blockId,
  suggested = null,
  onPick,
  t,
}: {
  blocks: { id: string; name: string }[];
  blockId: string | null;
  /** El sector que creen la voz, la tarea o el GPS (marcado, no elegido): se confirma con un toque (G). */
  suggested?: { blockId: string; from: "voice" | "task" | "gps" } | null;
  onPick: (id: string) => void;
  t: TFn;
}) {
  if (blocks.length === 0) {
    return (
      <p className="text-sm text-center text-muted-foreground py-8">
        {t("worker.incident.noBlocks")}
      </p>
    );
  }
  const sugerido = suggested ? blocks.find((b) => b.id === suggested.blockId) ?? null : null;
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        {t("worker.incident.pickBlock")}
      </h2>
      {/* Ya elegido (lo puso la voz, la tarea o el GPS, 28-09-2026): va marcado, sin «tócalo para confirmar». */}
      {sugerido && suggested && sugerido.id !== blockId && (
        <p className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900" data-testid="block-suggested" data-from={suggested.from} data-block-id={sugerido.id}>
          {t(suggested.from === "gps" ? "worker.incident.blockSuggestedGps" : suggested.from === "task" ? "worker.incident.blockSuggestedTask" : "worker.incident.blockSuggestedVoice", { name: sugerido.name })}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        {blocks.map((b) => (
          <BigOption
            key={b.id}
            label={b.name}
            active={b.id === blockId}
            suggested={!!sugerido && b.id === sugerido.id && b.id !== blockId}
            onClick={() => onPick(b.id)}
          />
        ))}
      </div>
    </div>
  );
}

function GpsBanner({
  geo,
  blocking,
  far,
  t,
}: {
  geo: ReturnType<typeof useGeolocation>;
  blocking: boolean;
  /** El fix sitúa al trabajador creíblemente lejos de la nave declarada. */
  far: boolean;
  t: TFn;
}) {
  if (geo.status === "watching" && geo.position) {
    // NUNCA bloquea (decisión usuario 2026-08-04): el reporte entra igual con
    // el foco degradado a "aproximado" sobre la línea declarada.
    if (far) {
      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 mb-3 flex items-center gap-2 text-xs font-medium text-amber-800">
          <MapPin className="w-4 h-4 shrink-0" />
          {t("worker.incident.gpsFarFromGreenhouse")}
        </div>
      );
    }
    if (geo.position.accuracyM > 50) {
      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 mb-3 flex items-center gap-2 text-xs font-medium text-amber-800">
          <MapPin className="w-4 h-4 shrink-0" />
          {t("worker.incident.gpsLowAccuracy", { m: Math.round(geo.position.accuracyM) })}
        </div>
      );
    }
    return null;
  }
  const searching = geo.status === "requesting" || geo.status === "idle";
  // Embebida en un iframe (p.ej. preview de Lovable) el navegador bloquea la
  // geolocalización SIN preguntar: el mensaje genérico de "denegado" confunde.
  const blockedByIframe = geo.inIframe && (geo.status === "denied" || geo.permission === "denied");
  const msg = blockedByIframe
    ? t("worker.incident.gpsIframe")
    : searching
      ? t("worker.incident.gpsSearching")
      : geo.status === "denied"
        ? t("worker.incident.gpsDenied")
        : t("worker.incident.gpsUnavailable");
  // Con el permiso denegado, la salida es ir a Ajustes del sistema: las
  // instrucciones cambian por SO (y Android exige "ubicación precisa" — elegir
  // "Aproximada" llega a la WebView como DENEGADO).
  const os = detectMobileOS();
  const help =
    geo.status === "denied" && !blockedByIframe
      ? os === "ios"
        ? t("worker.incident.gpsHelpIos")
        : os === "android"
          ? t("worker.incident.gpsHelpAndroid")
          : null
      : null;
  const retry = () => {
    void requestLocationPermission().finally(() => geo.retry());
  };
  if (!blocking) {
    // Admin/perito: informativo, el envío sigue permitido (el foco caerá al
    // punto medio de la línea declarada).
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 mb-3 flex items-center gap-2 text-xs font-medium text-amber-800">
        <MapPin className="w-4 h-4 shrink-0" />
        {msg}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-4 mb-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
        <MapPin className="w-4 h-4 shrink-0" />
        {t("worker.incident.gpsRequired")}
      </div>
      <p className="text-xs text-red-700 mt-1">{msg}</p>
      {help && <p className="text-xs text-red-700 mt-1">{help}</p>}
      {!searching && (
        <button type="button" onClick={retry} className="mt-2 text-xs font-bold text-red-800 underline">
          {t("worker.incident.gpsEnable")}
        </button>
      )}
    </div>
  );
}

function LineStep({
  lineOptions,
  lineNumber,
  suggested = null,
  suggestedFrom = null,
  accuracyM = null,
  onPick,
  t,
}: {
  lineOptions: number[];
  lineNumber: number | null;
  /** La línea que creen el GPS o la voz (marcada, no elegida): se confirma con un toque o se elige otra. */
  suggested?: number | null;
  suggestedFrom?: "voice" | "gps" | null;
  accuracyM?: number | null;
  onPick: (n: number) => void;
  t: TFn;
}) {
  if (lineOptions.length === 0) {
    return (
      <div className="rounded-2xl border border-[#b6dded] bg-white shadow-sm p-5 text-center">
        <p className="font-semibold mb-1">{t("worker.incident.noLines")}</p>
        <p className="text-sm text-muted-foreground">{t("worker.incident.noLinesHint")}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        {t("worker.incident.pickLine")}
      </h2>
      {suggested != null && lineOptions.includes(suggested) && (
        <p
          className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900"
          data-testid={suggestedFrom === "voice" ? "line-voice-suggested" : "line-gps-suggested"}
          data-line={suggested}
        >
          {suggestedFrom === "voice"
            ? t("worker.incident.lineSuggestedVoice", { line: suggested })
            : t("worker.incident.gpsSuggested", { line: suggested, m: accuracyM != null ? Math.round(accuracyM) : "?" })}
        </p>
      )}
      <div className="grid grid-cols-4 gap-2">
        {lineOptions.map((n) => {
          const active = lineNumber === n;
          const sugerida = !active && suggested === n;
          return (
            <button
              key={n}
              onClick={() => onPick(n)}
              data-suggested={sugerida ? "true" : undefined}
              className={cn(
                "aspect-square rounded-2xl bg-white border border-[#b6dded] shadow-sm flex items-center justify-center text-lg font-bold active:scale-[0.98]",
                active && "ring-2 ring-primary bg-primary/5",
                sugerida && "ring-2 ring-amber-400 bg-amber-50",
              )}
            >
              L{n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const SEVERITY_OPTIONS: Array<"low" | "medium" | "high" | "critical"> = ["low", "medium", "high", "critical"];

/** Lo que devuelve `voice-create-scouting` (ya validado allí: nunca aproxima nave ni sector). */
interface VoiceReport {
  location_id?: string | null;
  greenhouse_id?: string | null;
  block_ids?: string[] | null;
  observed_row?: number | null;
  /** El sector que dijo sin decir la nave: se busca por nombre en la nave del parte. */
  spoken_block_name?: string | null;
  pest_id?: string | null;
  pest_name?: string | null;
  pest_translation_key?: string | null;
  /** Lo que dijo de la plaga, tal cual (F7). */
  pest_spoken?: string | null;
  /** Lo dicho encaja en varias plagas de la empresa: no se elige ninguna (F7). */
  pest_ambiguous?: boolean;
  pest_candidates?: Array<{ id: string; name: string }>;
  severity?: string | null;
  location_warning?: unknown;
}

function SummaryStep({
  photoCount,
  audioSeconds,
  breadcrumb,
  t,
  canClassify = false,
  pests = [],
  selectedPestId = null,
  onPestSelect,
  severity = null,
  onSeverityChange,
  focusSlot = null,
}: {
  photoCount: number;
  audioSeconds: number;
  breadcrumb: string;
  t: TFn;
  canClassify?: boolean;
  pests?: Array<{ id: string; name: string; translation_key?: string | null }>;
  selectedPestId?: string | null;
  onPestSelect?: (pestId: string | null) => void;
  severity?: "low" | "medium" | "high" | "critical" | null;
  onSeverityChange?: (severity: "low" | "medium" | "high" | "critical" | null) => void;
  /** El campo «Foco» de la confirmación, cuando los focos están encendidos y hay plaga elegida. */
  focusSlot?: React.ReactNode;
}) {
  const captureSummary =
    audioSeconds > 0
      ? t("worker.incident.summaryAudio", { seconds: audioSeconds })
      : t("worker.incident.summaryPhotos", { count: photoCount });
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50/60 p-4 space-y-2">
        <div className="flex items-center gap-2 text-emerald-700 font-semibold">
          <Check className="w-5 h-5" />
          {t("worker.incident.summaryTitle")}
        </div>
        <p className="text-lg font-bold text-foreground">{captureSummary}</p>
        {breadcrumb && (
          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-4 h-4" />
            {breadcrumb}
          </p>
        )}
      </div>

      {canClassify && (
        <div className="rounded-2xl border border-[#b6dded] bg-white p-4 space-y-2">
          <p className="text-sm font-semibold text-foreground">
            {t("worker.incident.classifyOptional")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("worker.incident.classifyHint")}
          </p>
          <Select
            value={selectedPestId ?? "__none__"}
            onValueChange={(v) => onPestSelect?.(v === "__none__" ? null : v)}
          >
            <SelectTrigger className="bg-white shadow-sm" data-testid="wizard-pest-select">
              <SelectValue placeholder={t("worker.incident.classifyPestPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {/* Sin plaga es «No lo sé», como en el resumen de la voz (28-09-2026): opcional. */}
              <SelectItem value="__none__">{t("worker.incident.pestUnknown")}</SelectItem>
              {pests.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <p className="text-sm font-semibold text-foreground pt-1">
            {t("worker.incident.severityField")}
          </p>
          <Select
            value={severity ?? "__none__"}
            onValueChange={(v) => onSeverityChange?.(v === "__none__" ? null : (v as "low" | "medium" | "high" | "critical"))}
          >
            <SelectTrigger className="bg-white shadow-sm" data-testid="wizard-severity-select">
              <SelectValue placeholder={t("worker.incident.severityPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">—</SelectItem>
              {SEVERITY_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{t(`worker.incident.severityLevels.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {focusSlot}
        </div>
      )}
    </div>
  );
}

/**
 * Una fila de «Esto es lo que entendí». Se ve igual que la de solo lectura
 * (baldosa de color, etiqueta y valor) pero abre un desplegable pequeño con sus
 * opciones: corregir lo que la voz entendió mal se hace AQUÍ, sin ir a los pasos
 * de uno en uno.
 *
 * Sin opciones —un sector antes de haber elegido la nave— no es pulsable: la
 * cadena se rellena de arriba abajo y un desplegable vacío solo despista.
 */
interface EditableRowSpec {
  field: "location" | "greenhouse" | "block" | "line" | "pest" | "severity";
  icon: LucideIcon;
  label: string;
  value: string | null;
  tone?: string;
  emphasis?: boolean;
  options: Array<{ id: string; label: string }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Cuadrícula para códigos cortos (sectores, líneas, gravedad); lista para nombres. */
  layout?: "list" | "grid";
  cols?: 2 | 3 | 4;
  /** De dónde salió el sitio (voz, tarea, GPS); nada si lo eligió a mano (28-09-2026). */
  from?: PlacedBy;
}

/** La marca pequeña de «Lo dijiste tú» / «De tu tarea» / «Lo puso el GPS», junto a la etiqueta. */
const PLACED_TAG = {
  voice: { icon: Check, cls: "text-emerald-800", key: "worker.incident.place.fromVoice" },
  task: { icon: ClipboardList, cls: "text-sky-900", key: "worker.incident.place.fromTask" },
  gps: { icon: MapPin, cls: "text-amber-800", key: "worker.incident.place.fromGps" },
} as const;

const GRID_COLS: Record<2 | 3 | 4, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

function EditableSummaryRow({ spec, editLabel }: { spec: EditableRowSpec; editLabel: string }) {
  const { t: editLabelT } = useTranslation();
  const [open, setOpen] = useState(false);
  const Icon = spec.icon;
  // Sin opciones no hay lápiz: un sector antes de elegir la nave no se puede
  // tocar, y la cadena se rellena de arriba abajo.
  const canEdit = spec.options.length > 0;

  return (
    <div data-testid={`voice-row-${spec.field}`} data-value={spec.value ?? ""} data-from={spec.value && spec.from ? spec.from : ""}>
      <div className="w-full flex items-center gap-3 px-3 py-3">
        <div
          className={cn(
            "w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
            !spec.tone && "bg-[#eef4fb]",
          )}
          style={
            spec.tone
              ? { backgroundColor: spec.tone, boxShadow: `0 6px 14px ${spec.tone}42, inset 0 1px 0 rgba(255,255,255,0.28)` }
              : undefined
          }
        >
          <Icon className={cn("w-4 h-4", spec.tone ? "text-white" : "text-black")} />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="flex flex-wrap items-center gap-x-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
            {spec.label}
            {spec.value && spec.from && (() => {
              const tag = PLACED_TAG[spec.from];
              const TagIcon = tag.icon;
              return (
                <span className={cn("inline-flex items-center gap-0.5 normal-case tracking-normal", tag.cls)}>
                  <TagIcon className="h-3 w-3" />
                  {editLabelT(tag.key)}
                </span>
              );
            })()}
          </p>
          <div className={cn("text-sm truncate text-slate-800", spec.emphasis && "font-semibold")}>
            {spec.value || <span className="text-slate-400 italic">—</span>}
          </div>
        </div>
        {/* Se edita SOLO por el lápiz: con la fila entera pulsable se abría el
            selector cada vez que alguien la rozaba al desplazarse. */}
        {canEdit && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={editLabel}
            aria-expanded={open}
            className={cn(
              "w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 transition-colors active:scale-95",
              open
                ? "border-[#2E84DE] bg-[#2E84DE]/10 text-[#2E84DE]"
                : "border-slate-200 text-slate-500 hover:bg-slate-50",
            )}
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && canEdit && (
        <div className="px-3 pb-3">
          {spec.layout === "grid" ? (
            <div className={cn("grid gap-2", GRID_COLS[spec.cols ?? 2])}>
              {spec.options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => {
                    spec.onSelect(o.id);
                    setOpen(false);
                  }}
                  className={cn(
                    "min-h-11 px-2 py-2 rounded-2xl bg-white border border-[#b6dded] shadow-sm text-sm font-bold text-center active:scale-[0.98]",
                    o.id === spec.selectedId && "ring-2 ring-primary bg-primary/5",
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="max-h-[40vh] overflow-y-auto rounded-2xl border border-[#e9eff6] divide-y divide-[#eef3f8] bg-white">
              {spec.options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => {
                    spec.onSelect(o.id);
                    setOpen(false);
                  }}
                  className={cn(
                    "w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 active:bg-slate-100",
                    o.id === spec.selectedId && "font-semibold text-[#2E84DE]",
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Confirmación de lo que la IA entendió de la nota de voz, ya editable fila a
 * fila. Solo para manager/admin, antes de rellenar lo que falte.
 */
function VoicePreview({
  transcript,
  rows,
  warning,
  note = null,
  t,
}: {
  transcript: string;
  rows: EditableRowSpec[];
  warning: { type?: string; attempted_greenhouse?: string | null; resolved_location?: string | null } | null;
  /** Una línea bajo las filas (hoy: que sin plaga el parte se guarda sin clasificar). */
  note?: string | null;
  t: TFn;
}) {
  return (
    <div className="space-y-4" data-testid="voice-preview">
      {transcript && (
        <div className="w-full bg-white border border-[#e9eff6] rounded-2xl px-4 py-3">
          <p className="text-xs italic text-slate-500 leading-relaxed">"{transcript}"</p>
        </div>
      )}

      <div>
        {/* El título de la hoja ya dice «Esto es lo que entendí»: repetirlo aquí
            era leerlo dos veces seguidas. Se queda solo la indicación de qué
            hacer con ello. */}
        <p className="text-xs text-muted-foreground mb-2">
          {t("worker.incident.voicePreviewHint")}
        </p>
        <div className="bg-white rounded-2xl border border-[#e9eff6] divide-y divide-[#eef3f8] overflow-hidden">
          {rows.map((row, i) => (
            <EditableSummaryRow key={i} spec={row} editLabel={t("worker.incident.change")} />
          ))}
        </div>
        {note && (
          <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-600" data-testid="pest-unknown-hint">
            <CircleHelp className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {note}
          </p>
        )}
      </div>

      {warning?.attempted_greenhouse && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2.5 leading-relaxed">
          {t("worker.incident.voiceGreenhouseUnresolved", { name: warning.attempted_greenhouse })}
        </p>
      )}
    </div>
  );
}

/**
 * «EL GPS DICE OTRO SITIO» (Pablo, 24-09-2026). Un parte hecho tratando la Nave 4
 * acabó en la Nave 5 porque el GPS lo puso ahí sin decir nada. Ahora manda lo que
 * dijo o su tarea, y esto AVISA: qué dice el GPS, y un botón para ir a eso si es
 * verdad. No bloquea nada ni cambia nada solo.
 */
function GpsMismatchBanner({
  level,
  gpsName,
  hereName,
  onUseGps,
  t,
}: {
  level: "greenhouse" | "block";
  gpsName: string;
  hereName: string;
  onUseGps: () => void;
  t: TFn;
}) {
  return (
    <div className="mb-3 space-y-2 rounded-2xl border border-amber-300 bg-amber-50 p-3" data-testid="gps-mismatch" data-level={level} data-gps={gpsName}>
      <p className="flex items-start gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {t(level === "block" ? "worker.incident.place.gpsOtherBlock" : "worker.incident.place.gpsOtherGreenhouse", { gps: gpsName, here: hereName })}
        </span>
      </p>
      <button
        type="button"
        onClick={onUseGps}
        className="w-full rounded-xl border border-amber-300 bg-white px-3 py-2.5 text-sm font-bold text-amber-900 active:scale-[0.99]"
        data-testid="gps-mismatch-use"
      >
        {t(level === "block" ? "worker.incident.place.useGpsBlock" : "worker.incident.place.useGpsGreenhouse", { gps: gpsName })}
      </button>
    </div>
  );
}

/**
 * «¿DÓNDE ESTÁS?» DEL TRABAJADOR (W1/H, decisión de Pablo 23-09-2026).
 *
 * Tres filas —nave, sector, línea— y en cada una un semáforo que se entiende sin
 * leer mucho: ✓ verde «lo dijiste», 📍 ámbar «lo puso el GPS», ❓ rojo «no te he
 * entendido, lo eliges tú». Sale SIEMPRE que hubo nota, aunque no se entendiera
 * nada (antes pasaba en silencio a los pasos). Lo que sale aquí QUEDA PUESTO (Pablo,
 * 28-09-2026): no hay que volver a tocarlo para confirmarlo; «Cambiar» lleva a su
 * pantalla y ahí sí se elige tocando.
 * Si el GPS dice otro sitio, el aviso con su botón (`GpsMismatchBanner`, 24-09-2026):
 * antes, con naves distintas, se preguntaba con dos botones (decisión F).
 */
function WorkerPlacePreview({
  transcript,
  miss,
  missByGps = false,
  mismatch,
  rows,
  warning,
  onChange,
  t,
}: {
  transcript: string;
  miss: "empty" | "failed" | "skipped" | null;
  /** Lo que no se entendió lo ha puesto el GPS: se dice eso, no «elígelo tú». */
  missByGps?: boolean;
  /** El aviso de «el GPS dice otro sitio», ya montado (o nada). */
  mismatch: ReactNode;
  rows: { field: "greenhouse" | "block" | "line"; icon: LucideIcon; label: string; value: string | null; from: PlacedBy }[];
  /** «Cambiar» de una fila: a su pantalla, donde se elige tocando. */
  onChange: (field: "greenhouse" | "block" | "line") => void;
  warning: { attempted_greenhouse?: string | null } | null;
  t: TFn;
}) {
  const STATUS = {
    voice: { icon: Check, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", key: "worker.incident.place.fromVoice" },
    task: { icon: ClipboardList, cls: "bg-sky-50 text-sky-900 border-sky-200", key: "worker.incident.place.fromTask" },
    gps: { icon: MapPin, cls: "bg-amber-50 text-amber-900 border-amber-300", key: "worker.incident.place.fromGps" },
    none: { icon: CircleHelp, cls: "bg-red-50 text-red-800 border-red-200", key: "worker.incident.place.missing" },
  } as const;
  return (
    <div className="space-y-3" data-testid="voice-preview" data-miss={miss ?? ""}>
      {miss && (
        <p className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-800" data-testid="worker-place-miss" data-miss={miss}>
          <CircleHelp className="mt-0.5 h-5 w-5 shrink-0" />
          {t(missByGps ? "worker.incident.place.notUnderstoodGps" : miss === "failed" ? "worker.incident.place.failed" : miss === "skipped" ? "worker.incident.place.skipped" : "worker.incident.place.notUnderstood")}
        </p>
      )}
      {mismatch}
      {(
        <div className="bg-white rounded-2xl border border-[#e9eff6] divide-y divide-[#eef3f8] overflow-hidden">
          {rows.map((row) => {
            const s = STATUS[row.value ? row.from ?? "voice" : "none"];
            const Icon = row.icon;
            const Status = s.icon;
            return (
              <div key={row.field} className="flex items-center gap-3 px-3 py-3" data-testid={`voice-row-${row.field}`} data-value={row.value ?? ""} data-from={row.value ? row.from ?? "voice" : "none"}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-[#eef4fb]">
                  <Icon className="w-4 h-4 text-black" />
                </div>
                {/* De dónde salió, pequeño y junto a la etiqueta: el nombre necesita la fila entera (28-09-2026). */}
                <div className="flex-1 min-w-0">
                  <p className="flex flex-wrap items-center gap-x-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    {row.label}
                    <span className={cn("inline-flex items-center gap-0.5 normal-case tracking-normal", s.cls.split(" ").filter((c) => c.startsWith("text-")).join(" "))}>
                      <Status className="h-3 w-3" />
                      {t(s.key)}
                    </span>
                  </p>
                  <p className="text-base font-bold text-slate-900 truncate">{row.value ?? "—"}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onChange(row.field)}
                  className="shrink-0 rounded-xl border border-[#b6dded] bg-white px-3 min-h-[40px] text-sm font-semibold text-slate-800 active:scale-[0.98]"
                  data-testid={`voice-row-change-${row.field}`}
                >
                  {row.value ? t("worker.incident.place.change") : t("worker.incident.place.choose")}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-muted-foreground">{t("worker.incident.place.confirmNext")}</p>
      {warning?.attempted_greenhouse && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2.5 leading-relaxed">
          {t("worker.incident.voiceGreenhouseUnresolved", { name: warning.attempted_greenhouse })}
        </p>
      )}
      {transcript && (
        <div className="w-full bg-white border border-[#e9eff6] rounded-2xl px-4 py-3">
          <p className="text-xs italic text-slate-500 leading-relaxed">"{transcript}"</p>
        </div>
      )}
    </div>
  );
}

/**
 * Tarjeta de recapitulación del propio resumen: qué se ha capturado, dónde y
 * qué se ha visto. Va DEBAJO de las filas editables, en la misma pantalla —
 * antes esto vivía en un paso aparte que repetía la ubicación con botones de
 * «Cambiar», justo lo que ahora se hace arriba (QA usuario 2026-08-11).
 */
function PreviewResumen({
  audioSeconds,
  photoCount,
  breadcrumb,
  pestName,
  severityLabel,
  t,
}: {
  audioSeconds: number;
  photoCount: number;
  breadcrumb: string;
  pestName: string | null;
  severityLabel: string | null;
  t: TFn;
}) {
  const captureSummary =
    audioSeconds > 0
      ? t("worker.incident.summaryAudio", { seconds: audioSeconds })
      : t("worker.incident.summaryPhotos", { count: photoCount });
  return (
    <div className="rounded-2xl border border-emerald-300 bg-emerald-50/60 p-4 space-y-2">
      <div className="flex items-center gap-2 text-emerald-700 font-semibold">
        <Check className="w-5 h-5" />
        {t("worker.incident.summaryTitle")}
      </div>
      <p className="text-lg font-bold text-foreground">{captureSummary}</p>
      {breadcrumb && (
        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
          <MapPin className="w-4 h-4 shrink-0" />
          {breadcrumb}
        </p>
      )}
      {pestName && (
        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
          <Bug className="w-4 h-4 shrink-0" />
          {pestName}
        </p>
      )}
      {severityLabel && (
        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
          <Gauge className="w-4 h-4 shrink-0" />
          {severityLabel}
        </p>
      )}
    </div>
  );
}

function BigOption({
  label,
  active,
  suggested = false,
  onClick,
}: {
  label: string;
  active?: boolean;
  /** Lo que creen la voz o el GPS: marcado en ámbar, se confirma tocándolo (G). */
  suggested?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      data-suggested={suggested ? "true" : undefined}
      className={cn(
        "rounded-2xl bg-white border border-[#b6dded] shadow-sm p-4 flex items-center justify-center text-base font-semibold active:scale-[0.98] transition-transform min-h-[64px]",
        active && "ring-2 ring-primary bg-primary/5",
        suggested && "ring-2 ring-amber-400 bg-amber-50",
      )}
    >
      {label}
    </button>
  );
}
